import Constants from 'expo-constants';
import { Platform } from 'react-native';

import type { MobileNumberSetupSource } from '@/components/MobileNumberSetupForm';
import { ANALYTICS_EVENTS } from '@/constants/analytics';
import type { ModRoute } from '@/constants/medicalOnDemand';
import { INVITE_COPY_VERSION, type ReferralLocation } from '@/constants/referrals';
import type { SupportContactSource } from '@/features/support-contact/model';
import type {
  CitationRequestStatus,
  StatementTriageEntryMode,
  StatementTriageOpenSource,
} from '@/features/reference-assistant/model';
import { CognitoUser } from '@/hooks/useAuth';
import type { InquiryForwardTarget } from '@/services/conversations/inquiryForward';
import { segmentClient } from '@/util/analytics';
import { communicationAttributionProperties } from '@/util/communicationAttribution';
import type {
  CommunityPostReadExitReason,
  CommunityPostReadResult,
} from '@/util/communityPostReadSession';
import type { DeepLinkLaunchType, DeepLinkParams, DeepLinkType } from '@/util/deepLinkAnalytics';
import { isApiErrorV2 } from '@/util/fetchClient';
import { getFirstTouchAttribution } from '@/util/firstTouchAttribution';
import { Sentry } from '@/util/sentry';

export type { CitationRequestStatus };

type ApiErrorV2 = Parameters<typeof isApiErrorV2>[0] & {
  contentType: string;
  body: {
    success: boolean;
    error: string;
  };
  status: number;
};

export type InboxTabContext = 'all' | 'open' | 'closed' | 'drafts';

/**
 * Canonical inquiry-group analytics context shared across inquiry-group events
 * and related lifecycle events such as inbox view, inquiry submitted, message
 * sent, and inquiry closed.
 *
 * Property meanings:
 * - inquiryGroupsEnabled: True when grouped inbox and chat UI for inbox_inquiry_groups is active. Differs from anonymous fan-out on create and from sending anonymousFanOut on submit.
 * - inquiryParentId: Parent inquiry id used to group related inquiries.
 * - inquiryGroupSize: Total number of inquiries in the active group.
 * - isGroupedInquiry: Whether the action occurred on a grouped inquiry experience.
 * - inboxTab: Inbox tab context associated with the action.
 * - source: Entry point that triggered the action.
 */
export type InquiryGroupAnalyticsContext = {
  inquiryGroupsEnabled: boolean;
  inquiryParentId?: number | null;
  inquiryGroupSize?: number;
  isGroupedInquiry?: boolean;
  inboxTab?: InboxTabContext;
  source?: string;
};

/**
 * Properties for the "Inquiry group opened" event.
 *
 * Property meanings:
 * - conversationId: Selected conversation id that was opened.
 * - visibleInquiryGroupSize: Number of group items visible in the current view.
 * - hasUnread: Whether the opened group had unread activity.
 *
 * Includes all shared inquiry-group context properties.
 */
export type InquiryGroupOpenedProperties = InquiryGroupAnalyticsContext & {
  conversationId: string;
  visibleInquiryGroupSize?: number;
  hasUnread?: boolean;
};

/**
 * Properties for the "Inquiry group switched" event.
 *
 * Property meanings:
 * - fromConversationId: Conversation the user switched away from.
 * - toConversationId: Conversation the user switched to.
 *
 * Includes all shared inquiry-group context properties.
 */
export type InquiryGroupTabSwitchedProperties = InquiryGroupAnalyticsContext & {
  fromConversationId: string;
  toConversationId: string;
  /**
   * Where the tabs came from: `send` for the threads of one submission, `question` for every
   * expert thread on an inquiry group card. A `question` switch's parent id names the thread it
   * left, not a group, so analyses of send groups should filter on this.
   */
  tabGrouping?: 'question' | 'send';
};

function normalizeInquiryGroupAnalyticsContext(
  context: InquiryGroupAnalyticsContext
): InquiryGroupAnalyticsContext {
  const isGroupedInquiry =
    context.isGroupedInquiry ??
    (typeof context.inquiryGroupSize === 'number' ? context.inquiryGroupSize > 1 : undefined);

  return {
    ...context,
    isGroupedInquiry,
  };
}

/** A directory search was run for text the reader typed or submitted. */
export function trackDirectorySearchSubmitted(properties: { query: string }) {
  return segmentClient.track(ANALYTICS_EVENTS.SEARCH_SUBMITTED, properties);
}

/**
 * Tracks each individual MOD Direct-to-MSL route selection or deselection.
 * Fires per click in the route picker so funnels can distinguish "selected
 * MSL then deselected before submit" from "submitted with MSL selected."
 */
export function trackMODRouteToggled(properties: {
  route: ModRoute;
  action: 'selected' | 'deselected';
}) {
  return segmentClient.track(ANALYTICS_EVENTS.MOD_ROUTE_TOGGLED, properties);
}

/**
 * What kind of terminal Twilio failure the session was recovered from.
 *
 * `initFailed` is a client that never came up; `denied` is the SDK rejecting
 * the JWT on an established connection. Both are recovered the same way, but
 * they arrive from different points in the session lifecycle, so keeping them
 * distinct is what tells us which one actually happens in the wild.
 */
export type ChatSessionRecoverySource = 'initFailed' | 'denied' | 'networkDrop';

/**
 * Tracks a Twilio chat session that came back on its own after a terminal
 * token failure.
 *
 * The counterpart failure is reported to Sentry only once recovery is
 * exhausted, so without this event a successful recovery leaves no trace at
 * all and the two outcomes cannot be told apart. `attempts` is what says
 * whether one refresh is enough in practice or the budget is doing real work.
 *
 * `source` separates a rejected grant (`denied`, `initFailed`) from the socket
 * simply being gone (`networkDrop`). They arrive as the same terminal SDK
 * event and want the same recovery, but only the first pair says anything
 * about our tokens.
 */
export function trackChatSessionRecovered(properties: {
  source: ChatSessionRecoverySource;
  attempts: number;
  user_id?: string;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.CHAT_SESSION_RECOVERED, properties);
}

/**
 * One parallel-run comparison between the chat transport in charge and the one
 * being trialed. Counts and kinds only, never message content. Every
 * comparison is tracked, matches included, so the match rate has a
 * denominator; mismatches are also reported to Sentry.
 */
export function trackChatTransportShadowCompared(properties: {
  conversation_id: string;
  outcome: 'match' | 'mismatch' | 'shadow_failed';
  primary_kind?: string;
  shadow_kind?: string;
  compared: number;
  missing: number;
  extra: number;
  id_mismatch: number;
  body_mismatch: number;
  attribute_mismatch: number;
  differing_attribute_keys: string[];
}) {
  return segmentClient.track(ANALYTICS_EVENTS.CHAT_TRANSPORT_SHADOW_COMPARED, properties);
}

/**
 * Where an inquiry summary was read: the Inquiry Library post screen, or a Research
 * result. The values match the saved-post toggle's `source`, so the two join.
 */
export type InquirySummarySource = 'community' | 'library_chat';

/**
 * Tracks a reader opening the support form from the line a summary card shows after a
 * vote: `advisor` after a thumbs up, `feedback` after a thumbs down.
 */
export function trackInquirySummaryFollowUpOpened(properties: {
  post_id: number;
  kind: 'advisor' | 'feedback';
  source: InquirySummarySource;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.INQUIRY_SUMMARY_FOLLOW_UP_OPENED, properties);
}

/**
 * Tracks the support form opened from that line being sent. Fires only once the send
 * succeeds, so the gap between this and `follow-up opened` is readers who gave up.
 */
export function trackInquirySummaryFollowUpSubmitted(properties: {
  post_id: number;
  kind: 'advisor' | 'feedback';
  source: InquirySummarySource;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.INQUIRY_SUMMARY_FOLLOW_UP_SUBMITTED, properties);
}

/**
 * Tracks a reader rating the inquiry summary on an Inquiry Library post.
 *
 * `cleared` is a vote taken back, recorded rather than dropped: a reader who
 * mis-taps and undoes it should not count as an answer, and the only way to
 * know is to see the undo. `expanded` says whether they had the bullets open,
 * since a rating of the collapsed card is a rating of the conclusion alone.
 */
export function trackInquirySummaryRated(properties: {
  post_id: number;
  rating: 'up' | 'down' | 'cleared';
  expanded: boolean;
  source: InquirySummarySource;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.INQUIRY_SUMMARY_RATED, properties);
}

/*
 * Research events carry ids, counts, positions, stages and codes only.
 * Turn text, titles, facts and rationales are a clinician's own description of
 * a case and can carry patient detail, so no helper below accepts a string that
 * could hold them: the property types are the guard.
 */

/**
 * Tracks a press on the Inquiry Library's Research card, the top of the
 * Research funnel. It carries no conversation id because none exists yet;
 * the conversation that follows reports its own `entry`.
 */
export function trackLibraryChatEntryTriggered() {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_ENTRY_TRIGGERED);
}

/**
 * Tracks a conversation opening. `entry` separates a new conversation from one
 * reopened from history or from a link, which is how often saved history is
 * actually used.
 */
export function trackLibraryChatConversationOpened(properties: {
  conversation_id: string;
  turn_count: number;
  entry: 'new' | 'history' | 'link';
}) {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_CONVERSATION_OPENED, properties);
}

/**
 * Tracks a turn the server accepted. `is_follow_up` is false for a
 * conversation's first turn, the only turn `terms_accepted` is ever sent for.
 */
export function trackLibraryChatTurnSubmitted(properties: {
  conversation_id: string;
  turn_id: string;
  turn_index: number;
  text_length: number;
  is_retry: boolean;
  is_follow_up: boolean;
  terms_accepted?: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_TURN_SUBMITTED, properties);
}

/**
 * Tracks a turn this device submitted reaching its results, once, on that
 * device. `duration_ms` runs from the local send, so it includes queueing.
 */
export function trackLibraryChatTurnCompleted(properties: {
  conversation_id: string;
  turn_id: string;
  result_count: number;
  repeat_count: number;
  unavailable_count: number;
  context_changed: boolean;
  duration_ms: number;
}) {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_TURN_COMPLETED, properties);
}

/**
 * Tracks a turn failing, either refused at submit (`status` set) or failed by
 * the search after it was accepted (`error_code` from the snapshot).
 */
export function trackLibraryChatTurnFailed(properties: {
  conversation_id: string;
  turn_id: string;
  error_code: string;
  status?: number;
}) {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_TURN_FAILED, properties);
}

export type AskExpertSurface = 'thread' | 'summary' | 'composer';

/**
 * Tracks a reader taking a turn to an expert instead. `surface` separates the
 * button under a turn's own results, the one in the Summary panel, and the
 * "Start an inquiry" pill above the composer. `prefilled_cancer_type_id` is
 * the cancer type the form selects from the conversation, and
 * `has_cancer_type_hint` marks one it could only show as a hint, because no
 * id came with it or the form does not offer that id.
 */
export function trackLibraryChatAskExpertTriggered(properties: {
  conversation_id: string;
  turn_id: string;
  surface: AskExpertSurface;
  prefilled_cancer_type_id: number | null;
  has_cancer_type_hint: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_ASK_EXPERT_TRIGGERED, properties);
}

/**
 * Tracks an inquiry submitted after a handoff from an Ask conversation.
 * `cancer_type_changed` is how often the conversation's inferred cancer type
 * was wrong: the reader replaced the prefilled one before submitting.
 */
export function trackLibraryChatInquirySubmitted(properties: {
  conversation_id: string;
  inquiry_id: number | null;
  prefilled_cancer_type_id: number | null;
  submitted_cancer_type_id: number;
  cancer_type_changed: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_INQUIRY_SUBMITTED, properties);
}

/**
 * Tracks a reader opening one result. `position` is its 0-based rank in the
 * turn, the signal for whether ranking works; `is_repeat` separates a result an
 * earlier turn already showed.
 */
export function trackLibraryChatResultViewed(properties: {
  conversation_id: string;
  turn_id: string;
  source: string;
  result_id: string;
  position: number;
  is_repeat: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_RESULT_VIEWED, properties);
}

/** Tracks the Research tutorial opening on its first step. */
export function trackResearchTutorialStarted() {
  segmentClient.track(ANALYTICS_EVENTS.RESEARCH_TUTORIAL_STARTED);
}

/**
 * Tracks the reader leaving the Research tutorial before its last step.
 * `segment` and `step` name where they left, so drop-off can be read per step.
 */
export function trackResearchTutorialSkipped(properties: { segment: string; step: number }) {
  segmentClient.track(ANALYTICS_EVENTS.RESEARCH_TUTORIAL_SKIPPED, properties);
}

/** Tracks the reader finishing the Research tutorial's last step. */
export function trackResearchTutorialCompleted() {
  segmentClient.track(ANALYTICS_EVENTS.RESEARCH_TUTORIAL_COMPLETED);
}

/**
 * Tracks stepping to a neighboring result of a Research turn via the
 * Previous/Next footer; `result_id` is the result stepped to. Shares `COMMUNITY_POST_PREV_CLICKED`/
 * `COMMUNITY_POST_NEXT_CLICKED` with the Library post screen's own Prev/Next
 * (`components/communityPost.tsx`), since it's the same reader action from a
 * different surface — this call site adds properties that one omits.
 */
export function trackLibraryChatResultPostStepped(properties: {
  conversation_id: string;
  turn_id: string;
  source: string;
  result_id: string;
  direction: 'previous' | 'next';
}) {
  segmentClient.track(
    properties.direction === 'previous'
      ? ANALYTICS_EVENTS.COMMUNITY_POST_PREV_CLICKED
      : ANALYTICS_EVENTS.COMMUNITY_POST_NEXT_CLICKED,
    properties
  );
}

export function trackLibraryChatConversationRenamed(properties: { conversation_id: string }) {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_CONVERSATION_RENAMED, properties);
}

export function trackLibraryChatConversationDeleted(properties: { conversation_id: string }) {
  segmentClient.track(ANALYTICS_EVENTS.LIBRARY_CHAT_CONVERSATION_DELETED, properties);
}

type SupportThreadViewerRole = 'member' | 'support';

/**
 * Support-thread events carry ids, lengths and roles only. Message text never
 * leaves the device in analytics, because members write clinical detail into
 * these threads.
 */
export function trackSupportThreadOpened(properties: {
  conversation_id: string;
  viewer_role: SupportThreadViewerRole;
  message_count: number;
}) {
  segmentClient.track(ANALYTICS_EVENTS.SUPPORT_THREAD_OPENED, properties);
}

/**
 * Staff acting as Primum Support picked a member to message. `created` is
 * whether that member had no support thread before, which separates starting
 * a conversation from continuing one.
 */
export function trackSupportThreadStarted(properties: {
  conversation_id: string;
  member_id: number;
  created: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.SUPPORT_THREAD_STARTED, properties);
}

/**
 * A signed-in member followed a prompt to contact support and their support
 * thread opened. `source` names the prompt, so prompts that used to email
 * support stay comparable.
 */
export function trackSupportContactStarted(properties: {
  source: SupportContactSource;
  conversation_id: string;
  has_draft: boolean;
  has_inquiry: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.SUPPORT_CONTACT_STARTED, properties);
}

export function trackSupportMessageSent(properties: {
  conversation_id: string;
  message_id: string;
  text_length: number;
  is_retry: boolean;
  viewer_role: SupportThreadViewerRole;
  /** Whether staff attached an inquiry to the message. */
  has_reference: boolean;
  /** Whether Primum Support shared a survey in the message. */
  has_survey: boolean;
  /** Photos and files sent with the message. */
  file_count: number;
}) {
  segmentClient.track(ANALYTICS_EVENTS.SUPPORT_MESSAGE_SENT, properties);
}

export function trackSupportMessageFailed(properties: {
  conversation_id: string;
  message_id: string;
  error_code: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.SUPPORT_MESSAGE_FAILED, properties);
}

type SupportFileUploadProperties = {
  conversation_id: string;
  message_id: string;
  file_size: number;
  mime_type: string;
};

/**
 * One file of a support message uploaded. The same event the inquiry chat
 * reports, with `context` telling the two apart. The file's name is left out,
 * since a clinician may name a file after a patient.
 */
export function trackSupportFileUploadCompleted(properties: SupportFileUploadProperties) {
  segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_COMPLETED, {
    ...properties,
    is_video: properties.mime_type.startsWith('video/'),
    context: 'support_message',
  });
}

/** One file of a support message failed to upload, so the message was not sent. */
export function trackSupportFileUploadFailed(
  properties: SupportFileUploadProperties & { error_code: string }
) {
  segmentClient.track(ANALYTICS_EVENTS.FILE_UPLOAD_FAILED, {
    ...properties,
    context: 'support_message',
  });
}

/**
 * A survey shared in a support thread opened from its card. The member opening
 * it is the share's conversion; staff opening their own card is a preview, told
 * apart by `viewer_role`. Whether the member then answered is the survey's own
 * submission event.
 */
export function trackSupportSurveyOpened(properties: {
  conversation_id: string;
  message_id: string;
  survey_group_id: string;
  viewer_role: SupportThreadViewerRole;
}) {
  segmentClient.track(ANALYTICS_EVENTS.SUPPORT_SURVEY_OPENED, properties);
}

/**
 * A reader opening one of the two inquiries a forward note names: `new` is the
 * inquiry it was resubmitted as, `previous` the one that went unanswered.
 * `conversation_id` is the support thread, so opens join to the note that
 * offered them.
 */
export function trackSupportForwardInquiryOpened(properties: {
  conversation_id: string;
  message_id: string;
  inquiry_id: number;
  target: InquiryForwardTarget;
  viewer_role: SupportThreadViewerRole;
}) {
  segmentClient.track(ANALYTICS_EVENTS.SUPPORT_FORWARD_INQUIRY_OPENED, properties);
}

/**
 * Tracks the inquiry summary card appearing on an Inquiry Library post, once
 * per post. The denominator for every other summary event: without it a count
 * of ratings or opens cannot be read as a share of readers.
 */
export function trackInquirySummaryViewed(properties: {
  post_id: number;
  expert_count: number;
  topic_count: number;
  source: InquirySummarySource;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.INQUIRY_SUMMARY_VIEWED, properties);
}

/**
 * Tracks a reader opening or closing the inquiry summary. Opening is the
 * signal that the conclusion alone was not enough.
 */
export function trackInquirySummaryToggled(properties: {
  post_id: number;
  expanded: boolean;
  source: InquirySummarySource;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.INQUIRY_SUMMARY_TOGGLED, properties);
}

/**
 * Tracks when a user opens an inquiry group from the inbox list.
 */
export function trackInquiryGroupOpened(properties: InquiryGroupOpenedProperties) {
  return segmentClient.track(
    ANALYTICS_EVENTS.INQUIRY_GROUP_OPENED,
    normalizeInquiryGroupAnalyticsContext(properties)
  );
}

/**
 * Tracks when a user switches tabs within a grouped inquiry conversation.
 */
export function trackInquiryGroupTabSwitched(properties: InquiryGroupTabSwitchedProperties) {
  return segmentClient.track(
    ANALYTICS_EVENTS.INQUIRY_GROUP_SWITCHED,
    normalizeInquiryGroupAnalyticsContext(properties)
  );
}

/**
 * Tracks when an inquiry is closed. Call after a successful close (e.g. after updateInquiryV2 with state 'closed').
 */
export function trackInquiryClosed(
  conversationId: string,
  context?: InquiryGroupAnalyticsContext & { inquiryId?: number }
) {
  const { inquiryId, ...groupContext } = context ?? { inquiryGroupsEnabled: false };

  return segmentClient.track(ANALYTICS_EVENTS.INQUIRY_CLOSED, {
    conversation_id: conversationId,
    ...(inquiryId != null && { inquiry_id: inquiryId }),
    ...normalizeInquiryGroupAnalyticsContext(groupContext),
  });
}

/**
 * Shared identifiers attached to every extra-effort flag event so usage can be
 * attributed per inquiry and per expert during the pilot's abuse monitoring.
 */
export type OnYourHonorAnalyticsContext = {
  conversationId: string;
  inquiryId?: string | number;
  recipientId?: string | number;
};

function normalizeOnYourHonorContext(context: OnYourHonorAnalyticsContext) {
  return {
    conversation_id: context.conversationId,
    ...(context.inquiryId != null && { inquiry_id: context.inquiryId }),
    ...(context.recipientId != null && { recipient_id: context.recipientId }),
  };
}

/** Tracks the expert tapping the On Your Honor button, opening the inline confirm. */
export function trackOnYourHonorTriggered(
  context: OnYourHonorAnalyticsContext & { isMarked: boolean }
) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_TRIGGERED, {
    ...normalizeOnYourHonorContext(context),
    is_marked: context.isMarked,
  });
}

/** Tracks the off→on transition: the expert confirms the mark (accepts the inline confirm). */
export function trackOnYourHonorConfirmed(context: OnYourHonorAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_CONFIRMED, {
    ...normalizeOnYourHonorContext(context),
  });
}

/**
 * Tracks the on→off transition: the expert removes a mark they had set. The
 * symmetric counterpart to the confirm event, so removal rate and net-marked
 * state are derivable without inferring them from the button-tap event.
 */
export function trackOnYourHonorRemoved(context: OnYourHonorAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_REMOVED, {
    ...normalizeOnYourHonorContext(context),
  });
}

/** Tracks backing out of the inline confirm without marking — the confirm funnel's drop-off. */
export function trackOnYourHonorCancelled(context: OnYourHonorAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_CANCELLED, {
    ...normalizeOnYourHonorContext(context),
  });
}

/**
 * Tracks a committed extra-effort attestation: fired the first time the expert
 * sends a message while the badge is on. Deduped to once per inquiry (not per
 * send) by the caller, so this counts attestations, not keystrokes — the system
 * of record for the pilot's abuse monitoring.
 */
export function trackOnYourHonorSubmitted(context: OnYourHonorAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_SUBMITTED, {
    ...normalizeOnYourHonorContext(context),
  });
}

/**
 * Tracks the expert answering the post-mark reason survey. `reason` is the
 * chip id (or the free-text value); `is_custom` distinguishes a write-in from a
 * predefined chip so the two can be split in analysis without parsing the text.
 */
export function trackOnYourHonorReasonSubmitted(
  context: OnYourHonorAnalyticsContext & { reason: string; isCustom: boolean }
) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_REASON_SUBMITTED, {
    ...normalizeOnYourHonorContext(context),
    reason: context.reason,
    is_custom: context.isCustom,
  });
}

/** Tracks the post-mark reason sheet becoming visible after an inquiry is marked. */
export function trackOnYourHonorReasonViewed(context: OnYourHonorAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_REASON_VIEWED, {
    ...normalizeOnYourHonorContext(context),
  });
}

/**
 * Tracks the expert dismissing the reason sheet without recording a reason —
 * the drop-off counterpart to `trackOnYourHonorReasonSubmitted`, so the two
 * together give the answer-vs-skip split against the viewed denominator.
 */
export function trackOnYourHonorReasonDismissed(context: OnYourHonorAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_REASON_DISMISSED, {
    ...normalizeOnYourHonorContext(context),
  });
}

/** Tracks the one-time intro banner becoming visible to an eligible expert. */
export function trackOnYourHonorIntroViewed(context: OnYourHonorAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_INTRO_VIEWED, {
    ...normalizeOnYourHonorContext(context),
  });
}

/** Tracks the expert dismissing the one-time intro banner. */
export function trackOnYourHonorIntroDismissed(context: OnYourHonorAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.ON_YOUR_HONOR_INTRO_DISMISSED, {
    ...normalizeOnYourHonorContext(context),
  });
}

/**
 * Context attached to every reference-assistant callout event so the intro's
 * views and dismissals can be attributed per inquiry and per expert. The
 * one-time honorarium bonus is not tracked here — payment is not modeled in the
 * app, and the backend reconciles the doubling from the publish it records.
 */
export type ReferenceAssistantAnalyticsContext = {
  conversationId: string;
  inquiryId?: string | number;
  recipientId?: string | number;
};

function normalizeReferenceAssistantContext(context: ReferenceAssistantAnalyticsContext) {
  return {
    conversation_id: context.conversationId,
    ...(context.inquiryId != null && { inquiry_id: context.inquiryId }),
    ...(context.recipientId != null && { recipient_id: context.recipientId }),
  };
}

/** Tracks the one-time reference-assistant intro banner becoming visible to an eligible expert. */
export function trackReferenceAssistantIntroViewed(context: ReferenceAssistantAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.REFERENCE_ASSISTANT_INTRO_VIEWED, {
    ...normalizeReferenceAssistantContext(context),
  });
}

/** Tracks the expert dismissing the reference-assistant intro banner without using it. */
export function trackReferenceAssistantIntroDismissed(context: ReferenceAssistantAnalyticsContext) {
  return segmentClient.track(ANALYTICS_EVENTS.REFERENCE_ASSISTANT_INTRO_DISMISSED, {
    ...normalizeReferenceAssistantContext(context),
  });
}

/**
 * Identifies the user and fires `LOG_IN` on a successful sign-in.
 *
 * Stores the same traits as the session-init identify (`services/session.ts`)
 * so the profile isn't briefly thinner after login — except `state` and
 * `userType`, which live on the full user profile (not `CognitoUser`): the
 * session identify sets those and the enrichment trait-merge keeps a partial
 * identify from wiping them. First-touch attribution is `null` on native.
 *
 * The `LOG_IN` payload dual-emits canonical snake_case keys
 * (`user_id` / `user_email` / `user_name`) beside the legacy camelCase keys
 * (`userId` / `firstName` / `lastName`). The legacy keys are deprecated and
 * dropped once primum-infra reads the canonical ones; they carry identical
 * values, so never count them as distinct fields.
 */
export async function trackLoginSuccess(
  user: CognitoUser,
  source?: 'login_screen' | 'auth_modal' | 'callback',
  extraProperties?: Record<string, unknown>
) {
  if (!user.userId) {
    return;
  }

  const name = [user.firstName, user.lastName].filter(Boolean).join(' ');

  const firstTouch = getFirstTouchAttribution();

  segmentClient.identify(user.userId, {
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    name: name || undefined,
    appVersion: Constants.expoConfig?.version,
    platform: Platform.OS,
    platformVersion: Platform.Version,
    ...(firstTouch ?? {}),
  });

  segmentClient.track(ANALYTICS_EVENTS.LOG_IN, {
    user_id: user.userId,
    user_email: user.email,
    user_name: name || undefined,
    userId: user.userId,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    source,
    ...extraProperties,
  });
}

/**
 * Tracks a failed login attempt.
 *
 * React Native `Error` objects often lose their enumerable properties when stringified.
 * This function extracts useful information (name, message) manually so that it shows up
 * consistently across platforms (web, iOS, Android). The full error is also stringified
 * and included in the payload for debugging purposes.
 */
export function trackLoginFailed(
  error: unknown,
  email?: string,
  source?: 'login_screen' | 'auth_modal'
) {
  let errorName: string | undefined;

  let errorMessage: string | undefined;

  if (error && typeof error === 'object') {
    if ('name' in error && typeof (error as any).name === 'string') {
      errorName = (error as any).name;
    }

    if ('message' in error && typeof (error as any).message === 'string') {
      errorMessage = (error as any).message;
    }
  }

  const incorrectPassword = errorName === 'NotAuthorizedException';

  segmentClient.track(ANALYTICS_EVENTS.LOGIN_FAILED, {
    email,
    source,
    incorrectPassword,
    errorName,
    errorMessage,
    error: JSON.stringify(error, null, 2),
  });
}

/**
 * Someone started authenticating with a provider. Fires on the tap, before the
 * OAuth round trip, so it reports intent and never an outcome -- what the
 * attempt became is reported later by `Log in`, `Account linked`, or the
 * sign-up funnel steps.
 *
 * `source` names every screen that actually sends one. The union used to list
 * three while live traffic carried five, because three call sites reached
 * `segmentClient.track` directly and so never had to satisfy it. Keep new
 * entry points going through here: a widened union is a compile error at the
 * call site, where a direct `track` is a silent divergence nobody sees until
 * someone reads a month of production events.
 */
export function trackAuthTriggered({
  provider,
  source,
  referredBy,
  utmSource,
}: {
  provider: 'google';
  source:
    | 'login_screen'
    | 'create_account'
    | 'create_account_step_1'
    | 'email_link_screen'
    | 'marketing_site';
  referredBy?: string | null;
  utmSource?: string | null;
}) {
  segmentClient.track(ANALYTICS_EVENTS.AUTH_TRIGGERED, {
    provider,
    source,
    // Omitted rather than sent null when the caller has no attribution to
    // report, so the two marketing-site entry points keep the explicit nulls
    // they already send and the other three do not gain empty columns.
    ...(referredBy !== undefined ? { referred_by: referredBy } : {}),
    ...(utmSource !== undefined ? { utm_source: utmSource } : {}),
  });
}

/**
 * A marketing-site link arrived carrying an email, and the sign-up form filled
 * that field in. Nobody has authenticated, or tried to.
 *
 * This used to report as `Auth triggered` with `provider: 'password'`, which
 * counted every prefilled form as an authentication attempt in the funnel and
 * rendered in Slack as "Someone started password auth". It is a landing, so it
 * carries the referral and campaign tags it was really added for and no
 * provider at all.
 */
export function trackSignupEmailPrefilled({
  referredBy,
  utmSource,
}: {
  referredBy?: string | null;
  utmSource?: string | null;
}) {
  segmentClient.track(ANALYTICS_EVENTS.SIGNUP_EMAIL_PREFILLED, {
    source: 'marketing_site',
    referred_by: referredBy ?? null,
    utm_source: utmSource ?? null,
  });
}

export function trackEmailSigninLinkViewed() {
  segmentClient.track(ANALYTICS_EVENTS.EMAIL_SIGNIN_LINK_VIEWED);
}

export function trackEmailSigninLinkFormSubmitted(email: string) {
  segmentClient.track(ANALYTICS_EVENTS.EMAIL_SIGNIN_LINK_FORM_SUBMITTED, {
    email,
  });
}

/**
 * Tracks a community post view. Shared with the authenticated post screen and
 * the public shared-link viewer; `source` distinguishes the entry point and
 * `email` is included only when a share link carries an `?email=` recipient
 * parameter so views can be attributed back to the addressed recipient.
 *
 * `openedVia` says how the authenticated screen was reached: from the library
 * list, by paging with Previous or Next, or directly from a link, a push or a
 * restored tab. `listPosition` is the 1-based place in the list, so a report can
 * tell whether readers open more than the first few results.
 *
 * `postId` stays camelCase although newer properties are snake_case: it is the
 * key every existing report on this event filters by.
 */
export function trackCommunityPostViewed(properties: {
  postId: number;
  source?: string;
  email?: string;
  openedVia?: CommunityPostOpenedVia;
  listPosition?: number;
}) {
  segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_POST_VIEWED, {
    postId: properties.postId,
    type: 'inquiry',
    ...(properties.source ? { source: properties.source } : {}),
    ...(properties.email ? { email: properties.email } : {}),
    ...(properties.openedVia ? { opened_via: properties.openedVia } : {}),
    ...(properties.listPosition ? { list_position: properties.listPosition } : {}),
  });
}

export type CommunityPostOpenedVia = 'list' | 'pager' | 'direct';

/**
 * Tracks one stretch of reading an Inquiry Library post, sent when it ends.
 *
 * `active_seconds` is the number to report as reading time. It counts only
 * time near a scroll or touch, so a post left open does not inflate it.
 * `visible_seconds` is the wall-clock time on screen, kept alongside so the gap
 * between the two can be checked rather than assumed.
 *
 * Sent when the reader leaves, pages to another post, or backgrounds the app or
 * tab. Backgrounding ends the stretch rather than pausing it, because a
 * backgrounded app may never run again to send anything; a return starts a new
 * stretch marked `resumed`. Add stretches per post view when reporting totals.
 *
 * Depth is null when the answer never finished laying out, which is different
 * from a reader who saw none of it.
 */
export function trackCommunityPostRead(properties: {
  postId: number;
  openedVia: CommunityPostOpenedVia;
  exitReason: CommunityPostReadExitReason;
  resumed: boolean;
  result: CommunityPostReadResult;
  /** Which layout the post was read in, so reading can be compared across the two. */
  format?: PostFormat;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_POST_READ, {
    post_id: properties.postId,
    opened_via: properties.openedVia,
    exit_reason: properties.exitReason,
    resumed: properties.resumed,
    active_seconds: properties.result.activeSeconds,
    visible_seconds: properties.result.visibleSeconds,
    answer_seen_percent: properties.result.answerSeenPercent,
    comments_seen: properties.result.commentsSeen,
    ...(properties.format ? postFormatProperties(properties.format) : {}),
  });
}

/**
 * The layout an Inquiry Library post was shown in. `answers` is the question and one card per
 * specialist, `body` the single block of text. The counts are zero for `body`.
 */
export type PostFormat = {
  format: 'answers' | 'body';
  answerCount: number;
  /** Replies after each specialist's first, from either side, across the post. */
  replyCount: number;
  referenceCount: number;
};

const postFormatProperties = (format: PostFormat) => ({
  post_format: format.format,
  answer_count: format.answerCount,
  reply_count: format.replyCount,
  reference_count: format.referenceCount,
});

/**
 * The answers layout rendered for a reader, once per post on screen. It fires only when the flag
 * is on and the post has answers, which makes it the count of who has seen the new format.
 */
export function trackInquiryLibraryAnswersViewed(properties: {
  postId: number;
  format: PostFormat;
}) {
  segmentClient.track(ANALYTICS_EVENTS.INQUIRY_LIBRARY_ANSWERS_VIEWED, {
    postId: properties.postId,
    ...postFormatProperties(properties.format),
  });
}

/** A reader opened a reference's link under a specialist's answer. */
export function trackInquiryLibraryReferenceClicked(properties: {
  postId: number;
  specialistPosition: number;
  referencePosition: number;
  linkType: 'pubmed' | 'doi' | 'site';
}) {
  segmentClient.track(ANALYTICS_EVENTS.INQUIRY_LIBRARY_REFERENCE_CLICKED, {
    postId: properties.postId,
    specialist_position: properties.specialistPosition,
    reference_position: properties.referencePosition,
    link_type: properties.linkType,
  });
}

/** A reader showed every reference of a long list, or folded it back to three. */
export function trackInquiryLibraryReferencesExpanded(properties: {
  postId: number;
  specialistPosition: number;
  referenceCount: number;
  expanded: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.INQUIRY_LIBRARY_REFERENCES_EXPANDED, {
    postId: properties.postId,
    specialist_position: properties.specialistPosition,
    reference_count: properties.referenceCount,
    expanded: properties.expanded,
  });
}

/** A reader collapsed one specialist's card to its header, or opened it again. */
export function trackInquiryLibraryAnswerCollapsed(properties: {
  postId: number;
  specialistPosition: number;
  collapsed: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.INQUIRY_LIBRARY_ANSWER_COLLAPSED, {
    postId: properties.postId,
    specialist_position: properties.specialistPosition,
    collapsed: properties.collapsed,
  });
}

/** A reader pressed Collapse all or Expand all. `expanded` is the state it leaves the answers in. */
export function trackInquiryLibraryAnswersToggled(properties: {
  postId: number;
  expanded: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.INQUIRY_LIBRARY_ANSWERS_TOGGLED, {
    postId: properties.postId,
    expanded: properties.expanded,
  });
}

/**
 * Tracks a logged-in user being redirected from a public shared (gift) link to
 * the authenticated community post. Fires on the shared-link viewer before the
 * redirect; the destination post screen emits its own `COMMUNITY_POST_VIEWED`.
 * `email` is included only when the gift link carried an `?email=` recipient.
 */
export function trackCommunitySharedPostRedirected(properties: { postId: number; email?: string }) {
  segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_SHARED_POST_REDIRECTED, {
    postId: properties.postId,
    ...(properties.email ? { email: properties.email } : {}),
  });
}

/**
 * Tracks a like or unlike on an Inquiry Library post, once the request has
 * succeeded. `liked` is the state after the change.
 */
export function trackCommunityPostLikeToggled(properties: { postId: number; liked: boolean }) {
  segmentClient.track(
    properties.liked
      ? ANALYTICS_EVENTS.COMMUNITY_POST_LIKED
      : ANALYTICS_EVENTS.COMMUNITY_POST_UNLIKED,
    { type: 'post', uniqueId: properties.postId }
  );
}

/**
 * Tracks a save or unsave on an Inquiry Library post, once the request has
 * succeeded. `source` tells a save from Research apart from one in the library.
 */
export function trackCommunityPostSaveToggled(properties: {
  postId: number;
  saved: boolean;
  source: 'community' | 'library_chat';
}) {
  segmentClient.track(
    properties.saved
      ? ANALYTICS_EVENTS.COMMUNITY_POST_SAVED
      : ANALYTICS_EVENTS.COMMUNITY_POST_UNSAVED,
    { type: 'post', uniqueId: properties.postId, source: properties.source }
  );
}

const COMMUNITY_POST_SHARE_EVENTS = {
  started: ANALYTICS_EVENTS.COMMUNITY_POST_SHARE_STARTED,
  completed: ANALYTICS_EVENTS.COMMUNITY_POST_SHARE_COMPLETED,
  cancelled: ANALYTICS_EVENTS.COMMUNITY_POST_SHARE_CANCELLED,
} as const;

/** Tracks a step of sharing an Inquiry Library post through the system share sheet. */
export function trackCommunityPostShare(properties: {
  postId: number;
  stage: keyof typeof COMMUNITY_POST_SHARE_EVENTS;
}) {
  segmentClient.track(COMMUNITY_POST_SHARE_EVENTS[properties.stage], {
    type: 'post',
    uniqueId: properties.postId,
  });
}

/** Tracks the Inquiry Library list coming into view. */
export function trackCommunityViewed() {
  segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_VIEWED);
}

/** Tracks the cancer-type filter's options being opened on the Inquiry Library list. */
export function trackCommunityCancerTypesViewed() {
  segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_CANCER_TYPES_VIEWED);
}

/**
 * Tracks how far down the Inquiry Library list a reader has scrolled, each time
 * it passes its furthest point so far. `value` is that many posts.
 */
export function trackCommunityTotalPostsViewed(properties: {
  value: number;
  searchQuery: string;
  cancerTypeId: number;
  showSavedPosts: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_TOTAL_POSTS_VIEWED, properties);
}

/** Tracks a sort picked on the Inquiry Library list, or the change failing. */
export function trackCommunitySortBy(properties: { sortOption: string; error?: string }) {
  if (properties.error !== undefined) {
    segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_SORT_BY_FAILED, properties);

    return;
  }

  segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_SORT_BY, { sortOption: properties.sortOption });
}

/**
 * Tracks a step to the previous or next Inquiry Library post. Returns the
 * client's promise, which the caller swallows: a failed event must not stop
 * the step.
 */
export function trackCommunityPostStepped(properties: { direction: 'previous' | 'next' }) {
  return segmentClient.track(
    properties.direction === 'previous'
      ? ANALYTICS_EVENTS.COMMUNITY_POST_PREV_CLICKED
      : ANALYTICS_EVENTS.COMMUNITY_POST_NEXT_CLICKED
  );
}

/** Tracks a comment or reply saved, once the request has succeeded. */
export function trackCommentCreated(properties: {
  postId: number;
  replyToId: number | null;
  bodyLength: number;
  isAnonymous: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.COMMENT_CREATED, {
    post_id: properties.postId,
    reply_to_id: properties.replyToId,
    body_length: properties.bodyLength,
    is_anonymous: properties.isAnonymous,
  });
}

/** Tracks a comment or reply that failed to save, with the error. */
export function trackCommentCreateFailed(properties: {
  postId: number;
  replyToId: number | null;
  error: string;
  isAnonymous: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.COMMENT_CREATED_FAILED, {
    post_id: properties.postId,
    reply_to_id: properties.replyToId,
    error: properties.error,
    is_anonymous: properties.isAnonymous,
  });
}

/** Tracks a comment liked or unliked, once the request has succeeded. `liked` is the state after. */
export function trackCommentLikeToggled(properties: { commentId: number; liked: boolean }) {
  segmentClient.track(
    properties.liked ? ANALYTICS_EVENTS.COMMENT_LIKED : ANALYTICS_EVENTS.COMMENT_UNLIKED,
    { comment_id: properties.commentId }
  );
}

/** Tracks a like or unlike that failed. `liked` is the state the reader asked for. */
export function trackCommentLikeFailed(properties: {
  commentId: number;
  liked: boolean;
  error: string;
}) {
  segmentClient.track(
    properties.liked
      ? ANALYTICS_EVENTS.COMMENT_LIKED_FAILED
      : ANALYTICS_EVENTS.COMMENT_UNLIKED_FAILED,
    { comment_id: properties.commentId, error: properties.error }
  );
}

/** Tracks a comment or reply submitted on an Inquiry Library post. */
export function trackCommentSubmitted(properties: {
  postId: number;
  isReply: boolean;
  isAnonymous: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.COMMENT_SUBMITTED, { type: 'comment', ...properties });
}

/**
 * Tracks a community cancer-type filter selection. Shared by the mobile
 * dropdown and the web sidebar, which are separate widgets over the same
 * filter, so the label-resolution rule lives here once rather than being
 * duplicated (and drifting) per surface. `appliedCancerTypeId` is the filter
 * value after the interaction (0 when the type was toggled back off / cleared);
 * the reported `cancerType` is empty in that case, otherwise the type's label.
 */
export function trackCommunityCancerTypeSelected(properties: {
  appliedCancerTypeId: number;
  label: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_CANCER_TYPE_SELECTED, {
    cancerType: properties.appliedCancerTypeId === 0 ? '' : properties.label,
  });
}

/**
 * Tracks a community library query resolving: a search, a cancer-type filter, or
 * the saved-posts view.
 *
 * Fires once per request, so a search typed as `l` → `lu` → `lung` reports three
 * times. `searchSessionId` is what makes those three one search again:
 * count distinct it rather than counting events, and read `searchSequence` to
 * see how far a query had to be refined before it landed. Both are stable only
 * within a mounted screen, so treat a session as a typing run, not a visit.
 *
 * `resultCount` is the hit count for the whole query, not the page, and is
 * undefined when the api response omits it.
 */
/**
 * Tracks a post opened from a filtered or searched library list, the click
 * half of search analytics: which result, at what place, from which search.
 *
 * `search_session_id` and `search_sequence` match `searchSessionId` and
 * `searchSequence` on the `Community data requested` event for the list that
 * was on screen, so the two join on them. The casing differs because that event
 * predates the snake_case convention. Fired from the list rather than the post
 * screen, since only the list knows which search it is showing.
 */
export function trackCommunitySearchResultOpened(properties: {
  postId: number;
  listPosition: number;
  searchSessionId: string;
  searchSequence: number;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_SEARCH_RESULT_OPENED, {
    post_id: properties.postId,
    list_position: properties.listPosition,
    search_session_id: properties.searchSessionId,
    search_sequence: properties.searchSequence,
  });
}

export function trackCommunityDataRequested(properties: {
  cancerTypeId: number;
  searchQuery: string;
  showSavedPosts: boolean;
  resultCount?: number;
  searchSessionId: string;
  searchSequence: number;
}) {
  segmentClient.track(ANALYTICS_EVENTS.COMMUNITY_DATA_REQUESTED, {
    cancerTypeId: properties.cancerTypeId,
    searchQuery: properties.searchQuery,
    showSavedPosts: properties.showSavedPosts,
    resultCount: properties.resultCount,
    searchSessionId: properties.searchSessionId,
    searchSequence: properties.searchSequence,
  });
}

/**
 * Tracks a Content Library item view from the public shared-link viewer.
 * `email` is included only when a share link carries an `?email=` recipient
 * parameter so views can be attributed back to the addressed recipient.
 */
export function trackContentLibraryItemViewed(properties: {
  itemId: number;
  source?: string;
  email?: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_ITEM_VIEWED, {
    itemId: properties.itemId,
    ...(properties.source ? { source: properties.source } : {}),
    ...(properties.email ? { email: properties.email } : {}),
  });
}

/**
 * Tracks a logged-in user being redirected from a public shared (gift) link to
 * the authenticated Content Library item. Fires on the shared-link viewer before
 * the redirect; the destination item screen emits its own `CONTENT_LIBRARY_ITEM_VIEWED`.
 * `email` is included only when the gift link carried an `?email=` recipient.
 */
export function trackContentLibrarySharedItemRedirected(properties: {
  itemId: number;
  email?: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_SHARED_ITEM_REDIRECTED, {
    itemId: properties.itemId,
    ...(properties.email ? { email: properties.email } : {}),
  });
}

/**
 * Tracks an auto-shared Content Library suggestion card being shown to the
 * submitter in a chat thread. Fires once when the card mounts.
 */
export function trackContentSuggestionViewed(properties: {
  itemId: number;
  conversationId: string;
  messageSid: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.CONTENT_SUGGESTION_VIEWED, {
    item_id: properties.itemId,
    conversation_id: properties.conversationId,
    message_sid: properties.messageSid,
  });
}

/**
 * Tracks the submitter activating a content suggestion card's read call to
 * action, before navigation to the (gated) Content Library item.
 */
export function trackContentSuggestionClicked(properties: {
  itemId: number;
  conversationId: string;
  messageSid: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.CONTENT_SUGGESTION_CLICKED, {
    item_id: properties.itemId,
    conversation_id: properties.conversationId,
    message_sid: properties.messageSid,
  });
}

/**
 * Tracks the inline video on a chat content suggestion card starting playback.
 * The card autoplays on the tap that mounts the player, so this fires once when
 * the video actually begins — the true "auto-played" signal, distinct from the
 * generic Content Library video play event.
 */
export function trackContentSuggestionVideoPlayed(properties: {
  itemId: number;
  conversationId: string;
  messageSid: string;
  videoProvider: string;
  videoId: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.CONTENT_SUGGESTION_VIDEO_PLAYED, {
    item_id: properties.itemId,
    conversation_id: properties.conversationId,
    message_sid: properties.messageSid,
    video_provider: properties.videoProvider,
    video_id: properties.videoId,
  });
}

/**
 * Tracks the oncologist attestation gate being shown to an anonymous
 * Content Library visitor. `source` distinguishes the list, item, and
 * shared-link entry points.
 */
export function trackContentLibraryGateViewed(properties: { source: string }) {
  segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_GATE_VIEWED, {
    source: properties.source,
  });
}

/**
 * Tracks an anonymous visitor confirming the oncologist attestation.
 */
export function trackContentLibraryOncologistConfirmed(properties: { source: string }) {
  segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_ONCOLOGIST_CONFIRMED, {
    source: properties.source,
  });
}

/**
 * Tracks an anonymous visitor declining the oncologist attestation,
 * ahead of being routed to the partnership-interest survey.
 */
export function trackContentLibraryOncologistDeclined(properties: { source: string }) {
  segmentClient.track(ANALYTICS_EVENTS.CONTENT_LIBRARY_ONCOLOGIST_DECLINED, {
    source: properties.source,
  });
}

/**
 * Tracks an anonymous visitor being asked to sign in before answering a
 * survey that only accepts responses tied to an account. Pairs with
 * `CREATE_ACCOUNT_CTA_TRIGGERED` from the same screen to show how many of
 * these visitors go on to sign up rather than abandoning the survey.
 */
export function trackSurveyAuthGateViewed(properties: { surveyGroupId: string }) {
  segmentClient.track(ANALYTICS_EVENTS.SURVEY_AUTH_GATE_VIEWED, {
    survey_group_id: properties.surveyGroupId,
  });
}

/**
 * Tracks a respondent opening the list of polls they have answered. `source`
 * names the entry point, so the Account screen row and the web account menu
 * can be compared.
 */
export function trackPollHistoryViewed(properties: {
  source: 'account_screen' | 'web_navbar_menu';
}) {
  segmentClient.track(ANALYTICS_EVENTS.POLL_HISTORY_VIEWED, {
    source: properties.source,
  });
}

/**
 * Tracks a respondent opening one answered poll's results from their poll
 * history, as distinct from the results shown straight after voting.
 */
export function trackPollResultsViewed(properties: { surveyGroupId: string }) {
  segmentClient.track(ANALYTICS_EVENTS.POLL_RESULTS_VIEWED, {
    survey_group_id: properties.surveyGroupId,
    source: 'poll_history',
  });
}

export type SurveyPageViewedProperties = {
  surveyId: string;
  surveyName: string;
  surveyGroupId?: string;
  direction: 'next' | 'previous';
  fromPageName: string;
  fromPageIndex: number;
  toPageName: string;
  toPageIndex: number;
  toPageTitle?: string;
  totalPages: number;
};

function normalizeSurveyPageViewedProperties(properties: SurveyPageViewedProperties) {
  return {
    survey_id: properties.surveyId,
    survey_name: properties.surveyName,
    ...(properties.surveyGroupId != null && { survey_group_id: properties.surveyGroupId }),
    direction: properties.direction,
    from_page_name: properties.fromPageName,
    from_page_index: properties.fromPageIndex,
    to_page_name: properties.toPageName,
    to_page_index: properties.toPageIndex,
    ...(properties.toPageTitle != null && { to_page_title: properties.toPageTitle }),
    total_pages: properties.totalPages,
  };
}

/**
 * Tracks a respondent moving between survey pages, in either direction.
 * page_index is 0-based and counts only visible pages, matching what the
 * respondent actually sees as "page N of totalPages" in the stepper.
 * to_page_title is omitted when the destination page has no title (survey
 * pages are optionally titled).
 */
export function trackSurveyPageViewed(properties: SurveyPageViewedProperties) {
  segmentClient.track(
    ANALYTICS_EVENTS.SURVEY_PAGE_VIEWED,
    normalizeSurveyPageViewedProperties(properties)
  );
}

export function trackForgotPasswordRequested(email: string) {
  segmentClient.track(ANALYTICS_EVENTS.PASSWORD_FORGOT, {
    email,
  });
}

export function trackPasswordChanged(email?: string, source?: 'new-password' | 'create-password') {
  segmentClient.track(ANALYTICS_EVENTS.PASSWORD_CHANGED, {
    email,
    source,
  });
}

export function trackPasswordChangeFailed(
  error: Error,
  email?: string,
  source?: 'new-password' | 'create-password'
) {
  segmentClient.track(ANALYTICS_EVENTS.PASSWORD_CHANGE_FAILED, {
    email,
    source,
    error: JSON.stringify(error, null, 2),
  });
}

export function trackCreateAccountStep1Completed(data: { email: string }) {
  segmentClient.track(ANALYTICS_EVENTS.CREATE_ACCOUNT_STEP_1_COMPLETED, data);
}

export function trackCreateAccountReferredByBannerViewed(data: {
  referralCode: string;
  signupStep: 'step_1';
  referralId?: string | number;
  referrerId?: string | number;
  referrerName?: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.CREATE_ACCOUNT_REFERRED_BY_BANNER_VIEWED, {
    referral_code: data.referralCode,
    signup_step: data.signupStep,
    referral_id: data.referralId,
    referrer_id: data.referrerId,
    referrer_name: data.referrerName,
  });
}

/**
 * Tracks account creation event.
 *
 * data.userId can be numeric DB ID or Cognito UUID - see userIdAtom docs for details.
 *
 * data.userType is the underlying user model value ('lmp' or 'expert'). It is
 * normalized here to the title-cased values HubSpot expects ('LMP' / 'Expert')
 * before being forwarded via Segment, so the webapp_hubspot.ts subscription can
 * map it directly into the primum_user_type and primum_user_role contact
 * properties without any further transformation.
 *
 * accountCreatedDate is computed here as a local-timezone YYYY-MM-DD string
 * because HubSpot's `account_created_date` property is a Date picker (date
 * only, no time). Sending a full ISO timestamp would cause HubSpot to
 * normalize to UTC midnight, shifting evening-signup dates by a day for
 * users in US time zones. Computing the date from the user's local Date
 * components avoids that off-by-one.
 */
export function trackAccountCreated(data: {
  email: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  userType?: string;
  npiNumber?: string;
  state?: string;
  referredByName?: string;
  referredBy?: string;
  appGenerationIdentifier?: number;
}) {
  const { firstName, lastName, userType, ...rest } = data;

  const name = [firstName, lastName].filter(Boolean).join(' ');

  const hubSpotUserTypeMap: Record<string, string> = { lmp: 'LMP', expert: 'Expert' };

  const hubSpotUserType = userType ? hubSpotUserTypeMap[userType] : undefined;

  const now = new Date();

  const year = now.getFullYear();

  const month = String(now.getMonth() + 1).padStart(2, '0');

  const day = String(now.getDate()).padStart(2, '0');

  const accountCreatedDate = `${year}-${month}-${day}`;

  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_CREATED, {
    ...rest,
    firstName,
    lastName,
    name: name.length > 0 ? name : undefined,
    userType: hubSpotUserType,
    accountCreatedDate,
  });
}

/**
 * Tracks account creation event (unverified - before NPI verification).
 *
 * data.userId can be numeric DB ID or Cognito UUID - see userIdAtom docs for details.
 */
export function trackAccountCreatedUnverified(data: {
  email: string;
  userId?: string;
  referredByName?: string;
  referredBy?: string;
  appGenerationIdentifier?: number;
}) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_CREATED_UNVERIFIED, data);
}

export function trackAccountCreateErrored(data: {
  email: string;
  npiNumber?: string;
  error: Error | ApiErrorV2;
}) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_CREATE_ERRORED, {
    email: data.email,
    npiNumber: data.npiNumber,
    error: JSON.stringify(data.error, null, 2),
  });
}

export function trackSignupNpiValidationFailed(data: {
  npiNumber: string;
  error: Error | ApiErrorV2;
}) {
  segmentClient.track(ANALYTICS_EVENTS.NPI_VALIDATION_FAILED, {
    npiNumber: data.npiNumber,
    error: JSON.stringify(data.error, null, 2),
  });
}

export function trackNpiValidated(data: { npiNumber: string }) {
  segmentClient.track(ANALYTICS_EVENTS.NPI_VALIDATED, data);
}

export function trackNameEntered(data: { name: string }) {
  segmentClient.track(ANALYTICS_EVENTS.NAME_ENTERED, data);
}

export function trackUseTitleToggled(data: { identifiesAsDoctor: boolean }) {
  segmentClient.track(ANALYTICS_EVENTS.USE_TITLE_TOGGLED, data);
}

export function trackShowPasswordToggled(data: {
  isVisible: boolean;
  field: 'password' | 'confirmPassword';
}) {
  segmentClient.track(ANALYTICS_EVENTS.SHOW_PASSWORD_TOGGLED, data);
}

export function trackTermsClicked() {
  segmentClient.track(ANALYTICS_EVENTS.TERMS_CLICKED);
}

export function trackPrivacyClicked() {
  segmentClient.track(ANALYTICS_EVENTS.PRIVACY_CLICKED);
}

export function trackSupportModalOpened(data: {
  step: 'step1' | 'step2';
  trigger: 'npi_error' | 'email_error' | 'need_help';
}) {
  segmentClient.track(ANALYTICS_EVENTS.SUPPORT_MODAL_OPENED, data);
}

export function trackCreateAccountStep2BackTriggered() {
  segmentClient.track(ANALYTICS_EVENTS.CREATE_ACCOUNT_STEP_2_BACK_TRIGGERED);
}

export function trackCreateAccountStep2Completed(data: { email: string }) {
  segmentClient.track(ANALYTICS_EVENTS.CREATE_ACCOUNT_STEP_2_COMPLETED, data);
}

export function trackCreateAccountClosed(data: { step: 'step1' | 'step2' }) {
  segmentClient.track(ANALYTICS_EVENTS.CREATE_ACCOUNT_CLOSED, {
    step: data.step,
  });
}

export function trackCreateAccountCtaTriggered(data: {
  cta: 'join_primum' | 'sign_up';
  source:
    | 'landing_page'
    | 'auth_card'
    | 'login_page'
    | 'nav_bar'
    | 'auth_modal'
    | 'survey_auth_gate';
}) {
  segmentClient.track(ANALYTICS_EVENTS.CREATE_ACCOUNT_CTA_TRIGGERED, data);
}

export function trackNpiVerificationViewed() {
  segmentClient.track(ANALYTICS_EVENTS.NPI_VERIFICATION_VIEWED);
}

export function trackNpiReviewPendingViewed() {
  segmentClient.track(ANALYTICS_EVENTS.NPI_REVIEW_PENDING_VIEWED);
}

export function trackNpiVerificationCompleted(data: { name: string; npiNumber: string }) {
  segmentClient.track(ANALYTICS_EVENTS.NPI_VERIFICATION_COMPLETED, data);
}

export function trackNpiVerificationSkipClicked() {
  segmentClient.track(ANALYTICS_EVENTS.NPI_VERIFICATION_SKIP_CLICKED);
}

export function trackNpiVerificationSkipConfirmed() {
  segmentClient.track(ANALYTICS_EVENTS.NPI_VERIFICATION_SKIP_CONFIRMED);
}

export function trackNpiVerificationSkipCancelled() {
  segmentClient.track(ANALYTICS_EVENTS.NPI_VERIFICATION_SKIP_CANCELLED);
}

export function trackNpiVerificationFailed(error: unknown) {
  segmentClient.track(ANALYTICS_EVENTS.NPI_VERIFICATION_FAILED, {
    error: JSON.stringify(error, null, 2),
  });
}

export function trackAccountConflictDetected(data: {
  conflictType: 'NPI_IN_USE' | 'EMAIL_IN_USE';
  authProvider: 'google' | 'password';
  signupMethod: 'oauth' | 'password';
  source: 'npi_verification' | 'create_account_step_1' | 'create_account_step_2';
  userId?: string | null;
  email?: string | null;
}) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_CONFLICT_DETECTED, {
    conflictType: data.conflictType,
    authProvider: data.authProvider,
    signupMethod: data.signupMethod,
    source: data.source,
    user_id: data.userId || undefined,
    userId: data.userId || undefined,
    email: data.email || undefined,
  });
}

export function trackAccountLinkingInitiated(data: {
  conflictType: 'NPI_IN_USE' | 'EMAIL_IN_USE';
  source: 'npi_verification';
  authProvider: 'google' | 'password';
  userId?: string | null;
  email?: string | null;
}) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_LINKING_INITIATED, {
    conflictType: data.conflictType,
    source: data.source,
    authProvider: data.authProvider,
    user_id: data.userId || undefined,
    userId: data.userId || undefined,
    email: data.email || undefined,
  });
}

export function trackAccountLinked(data: {
  method: 'manual' | 'auto';
  provider: 'google';
  source: 'callback' | 'npi_verification' | 'login_screen';
  userId?: string | null;
  email?: string | null;
  linkStatus?: 'new' | 'linked';
}) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_LINKED, {
    method: data.method,
    provider: data.provider,
    source: data.source,
    user_id: data.userId || undefined,
    userId: data.userId || undefined,
    email: data.email || undefined,
    linkStatus: data.linkStatus || 'new',
  });
}

export function trackAccountLinkingFailed(data: {
  method: 'manual' | 'auto';
  provider: 'google';
  source: 'callback' | 'login_screen';
  error: string;
  userId?: string | null;
  email?: string | null;
}) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_LINKING_FAILED, {
    method: data.method,
    provider: data.provider,
    source: data.source,
    error: data.error,
    user_id: data.userId || undefined,
    userId: data.userId || undefined,
    email: data.email || undefined,
  });
}

/**
 * Returns user-facing title and subtitle for a magic link error screen.
 */
export function getMagicLinkErrorCopy(errType: string | undefined): {
  title: string;
  subtitle: string;
} {
  if (errType === 'expired_or_invalid') {
    return {
      title: 'Your sign-in link has expired or is invalid.',
      subtitle: 'Request a new link or sign in with your password.',
    };
  }

  if (errType === 'missing_params') {
    return {
      title: 'This sign-in link is incomplete.',
      subtitle: 'Try signing in again or contact support and we\u2019ll help you.',
    };
  }

  return {
    title: 'We couldn\u2019t complete your sign-in.',
    subtitle: 'Try signing in again or contact support and we\u2019ll help you.',
  };
}

/**
 * Tracks an auth callback error (magic link or OAuth).
 *
 * Logs to console, fires a LOGIN_FAILED analytics event, and reports to Sentry.
 * Uses captureException when an Error object is available, captureMessage otherwise.
 */
export function trackAuthCallbackError(data: {
  method: 'magic_link' | 'oauth' | 'smart_ehr';
  source: string;
  reason: string;
  /** Where in the callback flow the failure was raised, for telling shared reasons apart. */
  stage?: string;
  /** Whether the callback URL carried an OAuth code: a reload of a used URL often does not. */
  hasCode?: boolean;
  error?: unknown;
  extra?: Record<string, unknown>;
}) {
  console.error(`[Auth] ${data.method} callback error: ${data.reason}`, data.error ?? '');

  segmentClient.track(ANALYTICS_EVENTS.LOGIN_FAILED, {
    method: data.method,
    source: data.source,
    reason: data.reason,
    stage: data.stage,
    hasCode: data.hasCode,
  });

  if (data.error) {
    Sentry.captureException(data.error, {
      tags: { auth_flow: data.method },
      extra: data.extra,
    });
  } else {
    Sentry.captureMessage(`[Auth] ${data.method} callback error: ${data.reason}`);
  }
}

/**
 * Tracks an EHR launch that ended on the SMART error screen. `reason` is the API's refusal
 * code, or `expired` when the app itself lost the binding or failed to redeem the token.
 */
export function trackSmartLaunchFailed(data: { reason: string; embedded: boolean }) {
  segmentClient.track(ANALYTICS_EVENTS.SMART_LAUNCH_FAILED, {
    reason: data.reason,
    embedded: data.embedded,
  });
}

/**
 * Tracks an impression of the extracted-questions chat bubble — fires
 * once per mount per message when an LMP message with approved
 * extracted questions becomes visible to the viewer.
 *
 * Auto-injects `inquiry_id`, `conversation_id`, etc. when called via
 * `useChatAnalytics`. When called directly through `segmentClient`,
 * those dimensions are not attached.
 */
export function trackExtractedQuestionsRendered(
  track: typeof segmentClient.track,
  data: {
    messageSid: string;
    questionCount: number;
  }
) {
  track(ANALYTICS_EVENTS.EXTRACTED_QUESTIONS_RENDERED, {
    message_sid: data.messageSid,
    question_count: data.questionCount,
  });
}

/**
 * Channel that originated a references-ready deep link tap. `undefined`
 * when opened from a URL without a `source` query param (older
 * notifications, manual paste, or a different entry point).
 */
export type ReferencesReadyDeepLinkSource = 'email' | 'sms' | 'push';

/**
 * Tracks a successful end-to-end open of the citation review sheet via
 * the `referencesReady` push/email/SMS deep link. Fires from
 * `useAutoOpenReferencesReady` after the conversation's awaiting-review
 * citation request has been resolved and the triage sheet's open action
 * has been dispatched — i.e., only when the deep link actually delivered
 * the user into the review surface, not just when they tapped a link.
 */
export function trackReferencesReadyDeepLinkOpened(data: {
  conversationId: string;
  citationRequestId: string;
  messageSid: string;
  source?: ReferencesReadyDeepLinkSource;
}) {
  segmentClient.track(ANALYTICS_EVENTS.REFERENCES_READY_DEEP_LINK_OPENED, {
    conversation_id: data.conversationId,
    citation_request_id: data.citationRequestId,
    message_sid: data.messageSid,
    source: data.source,
  });
}

/**
 * Tracks an impression of the "Add references" prompt bubble — the
 * pill-shaped CTA rendered immediately under an expert author's
 * message when it has approved extracted statements but no attached
 * references yet. Fires once per mount per message.
 */
export function trackAddReferencesPromptViewed(
  track: typeof segmentClient.track,
  data: {
    messageSid: string;
    conversationId: string;
    totalStatements: number;
  }
) {
  track(ANALYTICS_EVENTS.ADD_REFERENCES_PROMPT_VIEWED, {
    message_sid: data.messageSid,
    conversation_id: data.conversationId,
    total_statements: data.totalStatements,
  });
}

/**
 * Tracks an impression of the message-processing status chip — the
 * author-only chip that surfaces backend-driven async processing
 * state (currently the inquiry-pipeline's classify/extract/link
 * stages for expert answers). Fires once per message mount, captured
 * at the earliest status the chip surfaces so we can measure how
 * often the chip is even seen in the wild.
 */
export function trackMessageProcessingSurfaced(
  track: typeof segmentClient.track,
  data: {
    messageSid: string;
    conversationId: string;
    status: string;
  }
) {
  track(ANALYTICS_EVENTS.MESSAGE_PROCESSING_SURFACED, {
    message_sid: data.messageSid,
    conversation_id: data.conversationId,
    status: data.status,
  });
}

/**
 * Role of the viewer who saw the extracted-statements bubble, relative to
 * the message author. `recipient` is the submitter side — the audience the
 * citation feature is ultimately for — so dashboards filter on it to build
 * the "published references shown to the submitter" denominator.
 */
export type ExtractedStatementsViewerRole = 'author' | 'recipient';

/**
 * Tracks an impression of the extracted-statements chat bubble — fires
 * at most once per message per app session when an expert-answer message
 * with approved extracted statements becomes visible to the viewer.
 *
 * `referenceCount` / `statementsWithReferences` describe how many
 * published references the viewer was actually shown, and `viewerRole`
 * identifies the audience. Together with `has_references` (derived) these
 * give a clean recipient-side denominator: "of submitters shown an answer
 * that had published citations, how many tapped a reference?"
 * (`REFERENCE_VIEWED` is the numerator). Without this, the bubble
 * impression fired regardless of whether any reference existed.
 *
 * Auto-injects `inquiry_id`, `conversation_id`, etc. when called via
 * `useChatAnalytics`. When called directly through `segmentClient`,
 * those dimensions are not attached.
 */
export function trackExtractedStatementsRendered(
  track: typeof segmentClient.track,
  data: {
    messageSid: string;
    statementCount: number;
    referenceCount: number;
    statementsWithReferences: number;
    viewerRole: ExtractedStatementsViewerRole;
  }
) {
  track(ANALYTICS_EVENTS.EXTRACTED_STATEMENTS_RENDERED, {
    message_sid: data.messageSid,
    statement_count: data.statementCount,
    reference_count: data.referenceCount,
    statements_with_references: data.statementsWithReferences,
    has_references: data.referenceCount > 0,
    viewer_role: data.viewerRole,
  });
}

/**
 * Tracks a reader opening a reference's source from the message reference
 * sheet.
 *
 * `total_attached_on_message` keeps the meaning it had when the footer opened
 * links directly — the statement footnotes on the message — so per-source
 * dashboards do not jump at release. `total_on_message` counts every
 * reference the sheet pages through, works only named in the text included.
 *
 * `reference_url` is the link's origin and path only. A share can be a link
 * the expert pasted, whose query can carry a patient search or a token, and a
 * link opened from the message text is reported the same way.
 */
export function trackReferenceViewed(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    url: string;
    urlHost?: string;
    referenceNumber: number;
    totalAttachedOnMessage: number;
    totalOnMessage: number;
    statementIndex?: number;
    statementType?: string;
    shareOrigin?: string;
    publicationKind?: string;
  }
) {
  const isStatementReference = data.statementIndex !== undefined;

  track(ANALYTICS_EVENTS.REFERENCE_VIEWED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    reference_url: withoutQuery(data.url),
    url_host: data.urlHost,
    reference_number: data.referenceNumber,
    total_attached_on_message: data.totalAttachedOnMessage,
    total_on_message: data.totalOnMessage,
    reference_source: isStatementReference ? 'statement_attached' : 'message_publication',
    ...(isStatementReference && {
      statement_index: data.statementIndex,
      statement_type: data.statementType,
    }),
    has_publication: data.shareOrigin !== undefined,
    ...(data.shareOrigin && { share_origin: data.shareOrigin }),
    ...(data.publicationKind && { publication_kind: data.publicationKind }),
  });
}

const withoutQuery = (url: string) => url.split(/[?#]/)[0];

export type ReferenceDetailsEntryPoint =
  | 'footnote'
  | 'navigation'
  | 'inline_citation'
  | 'statement_chip'
  | 'statement_text';

/**
 * Tracks a reference shown in the message reference sheet, both when a
 * footnote opens the sheet and when the reader pages to another reference.
 * `Reference viewed` stays the event for actually opening the source, so the
 * two together separate reading the details from following the link.
 * `entry_point` says what opened it: a footer row, the sheet's own paging, a
 * number placed in the message text, or a statement's `[N]` chip or
 * underlined words.
 */
export function trackReferenceDetailsViewed(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    referenceNumber: number;
    totalOnMessage: number;
    entryPoint: ReferenceDetailsEntryPoint;
    hasPublication: boolean;
    shareOrigin?: string;
    publicationKind?: string;
  }
) {
  track(ANALYTICS_EVENTS.REFERENCE_DETAILS_VIEWED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    reference_number: data.referenceNumber,
    total_on_message: data.totalOnMessage,
    entry_point: data.entryPoint,
    has_publication: data.hasPublication,
    ...(data.shareOrigin && { share_origin: data.shareOrigin }),
    ...(data.publicationKind && { publication_kind: data.publicationKind }),
  });
}

/**
 * Discriminator for failed citation-triage saves, so the analytics
 * payload can describe the failure without leaking exception types.
 */
export type StatementCitationTriagePersistErrorKind = 'persist_failed';

/**
 * Fires when the reference wizard opens for an expert message — for
 * **both** entry paths. `entry_mode` discriminates them: `search` (the
 * auto-start "Have Primum find references" flow) vs `provide` (the
 * "I have references in mind" flow). This is the canonical cross-path
 * "wizard opened" event; break it down by `entry_mode` to compare entry
 * usage. The provide path additionally fires the dedicated
 * `PROVIDE_REFERENCES_OPENED` (its funnel's own start, paired with
 * `PROVIDE_REFERENCES_PUBLISHED`/`_DISMISSED`), so the two co-fire on a
 * provide open — do not sum them when sizing total opens.
 *
 * `checked_count_before` snapshots the bubble's already-flagged count at
 * open time; on the search path the gap between it and
 * `checked_count_after` on the paired `STATEMENT_CITATION_TRIAGE_SAVED`
 * event reveals intent edits during the session.
 */
export function trackStatementCitationTriageOpened(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    statementCount: number;
    checkedCountBefore: number;
    entryMode: StatementTriageEntryMode;
    openSource: StatementTriageOpenSource;
  }
) {
  track(ANALYTICS_EVENTS.STATEMENT_CITATION_TRIAGE_OPENED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    statement_count: data.statementCount,
    checked_count_before: data.checkedCountBefore,
    entry_mode: data.entryMode,
    open_source: data.openSource,
  });
}

/**
 * Fires after a successful `Save` write returns from the
 * citation-triage action sheet. `newly_checked_count` and
 * `newly_unchecked_count` count only the statements whose values
 * changed in this commit; `checked_count_after` reflects the new
 * persisted total.
 */
export function trackStatementCitationTriageSaved(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    newlyCheckedCount: number;
    newlyUncheckedCount: number;
    checkedCountAfter: number;
  }
) {
  track(ANALYTICS_EVENTS.STATEMENT_CITATION_TRIAGE_SAVED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    newly_checked_count: data.newlyCheckedCount,
    newly_unchecked_count: data.newlyUncheckedCount,
    checked_count_after: data.checkedCountAfter,
  });
}

/**
 * Fires when the citation-triage wizard closes without completing —
 * Cancel, swipe-down, outside-click, or close button. `pending_change_count`
 * is retained for dashboard continuity but is always `0` now that the
 * statement picker (and its pending-changes map) has been removed; the
 * wizard auto-starts the search and has no local toggle state.
 */
export function trackStatementCitationTriageDismissed(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    pendingChangeCount: number;
  }
) {
  track(ANALYTICS_EVENTS.STATEMENT_CITATION_TRIAGE_DISMISSED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    pending_change_count: data.pendingChangeCount,
  });
}

/**
 * Fires when the citation wizard's provide phase opens — i.e. the
 * expert tapped "I have references in mind" on the bubble. The provide
 * flow is a single step (no selection), so `statement_count` is the
 * total number of extracted statements on the message — the denominator
 * for fill-rate against `PROVIDE_REFERENCES_PUBLISHED.url_count` +
 * `help_request_count`.
 */
export function trackProvideReferencesOpened(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    statementCount: number;
  }
) {
  track(ANALYTICS_EVENTS.PROVIDE_REFERENCES_OPENED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    statement_count: data.statementCount,
  });
}

/**
 * Fires after a successful provide-phase publish. `url_count` is the
 * number of verbatim references attached; `help_request_count` is the
 * number of describe-it rows persisted as help requests; their sum is
 * the filled-row count. `statement_count` is the total number of
 * extracted statements on the message, so fill-rate is self-contained
 * on this event without a join back to `PROVIDE_REFERENCES_OPENED`.
 */
export function trackProvideReferencesPublished(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    statementCount: number;
    urlCount: number;
    helpRequestCount: number;
  }
) {
  track(ANALYTICS_EVENTS.PROVIDE_REFERENCES_PUBLISHED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    statement_count: data.statementCount,
    url_count: data.urlCount,
    help_request_count: data.helpRequestCount,
  });
}

/**
 * Fires when a provide-phase publish write throws. The publish path
 * routes plain text to a help request rather than rejecting it, so the
 * only reachable failure is the Twilio attribute write — hence a single
 * `persist_failed` kind (no `invalid_url`, unlike the toolbar attach
 * flow).
 */
export function trackProvideReferencesPublishFailed(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    errorKind: 'persist_failed';
  }
) {
  track(ANALYTICS_EVENTS.PROVIDE_REFERENCES_PUBLISH_FAILED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    error_kind: data.errorKind,
  });
}

/**
 * Fires when the expert closes the provide flow without a successful
 * publish (✕, backdrop, Escape, or Cancel). Pairs with
 * `PROVIDE_REFERENCES_OPENED` to measure drop-off: `filled_count` is how
 * many rows carried content at dismiss time (0 = abandoned before
 * typing anything; >0 = intent-to-publish then bailed), and
 * `statement_count` is the total extracted statements available.
 */
export function trackProvideReferencesDismissed(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    statementCount: number;
    filledCount: number;
  }
) {
  track(ANALYTICS_EVENTS.PROVIDE_REFERENCES_DISMISSED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    statement_count: data.statementCount,
    filled_count: data.filledCount,
  });
}

/**
 * Fires when the Twilio attribute write triggered by `Save` throws.
 * The triage sheet remains open after this event; pending changes are
 * preserved so the author can retry.
 */
export function trackStatementCitationTriagePersistFailed(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    errorKind: StatementCitationTriagePersistErrorKind;
  }
) {
  track(ANALYTICS_EVENTS.STATEMENT_CITATION_TRIAGE_PERSIST_FAILED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    error_kind: data.errorKind,
  });
}

/** Failure modes for citation-request create mutations. Kept open-string so the
 * backend can surface new error kinds without a frontend release. */
export type CitationRequestErrorKind = 'persist_failed' | 'unknown' | (string & {});

/** Failure modes specific to the approve/publish mutation. Closed union — the
 * three cases are fully enumerated on the frontend side. */
export type CitationRequestApproveErrorKind = 'persist_failed';

/**
 * Fires when the expert successfully creates a citation request. The
 * wizard now auto-starts this on open (no statement picker), so it is the
 * first event in the reference flow for a message; join to the lifecycle
 * events by `message_sid`.
 */
export function trackCitationRequestCreated(
  track: typeof segmentClient.track,
  data: {
    citationRequestId: string;
    conversationId: string;
    messageSid: string;
    statementCount: number;
  }
) {
  track(ANALYTICS_EVENTS.CITATION_REQUEST_CREATED, {
    citation_request_id: data.citationRequestId,
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    statement_count: data.statementCount,
  });
}

/**
 * Fires when the auto-start `POST /v2/citation-requests` mutation throws.
 * The wizard stays open showing the error with a "Try again" retry.
 */
export function trackCitationRequestCreateFailed(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    errorKind: CitationRequestErrorKind;
  }
) {
  track(ANALYTICS_EVENTS.CITATION_REQUEST_CREATE_FAILED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    error_kind: data.errorKind,
  });
}

/**
 * Fires when the expert lands on the wizard's review step for an
 * `awaiting_review` request — either via the searching→review
 * transition or by tapping the chat-bubble pill on a request that
 * was already in review. `candidate_count` is the total proposed
 * references across all statements at the moment of open.
 */
export function trackCitationRequestReviewOpened(
  track: typeof segmentClient.track,
  data: {
    citationRequestId: string;
    conversationId: string;
    messageSid: string;
    candidateCount: number;
  }
) {
  track(ANALYTICS_EVENTS.CITATION_REQUEST_REVIEW_OPENED, {
    citation_request_id: data.citationRequestId,
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    candidate_count: data.candidateCount,
  });
}

/**
 * Fires after the expert publishes their approved references. Counts
 * are reported separately so dashboards can see approval rate without
 * cross-referencing the proposed list.
 */
export function trackCitationRequestApproved(
  track: typeof segmentClient.track,
  data: {
    citationRequestId: string;
    conversationId: string;
    messageSid: string;
    approvedCount: number;
    rejectedCount: number;
  }
) {
  track(ANALYTICS_EVENTS.CITATION_REQUEST_APPROVED, {
    citation_request_id: data.citationRequestId,
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    approved_count: data.approvedCount,
    rejected_count: data.rejectedCount,
  });
}

/**
 * Fires when the approve mutation throws. The review sheet stays open
 * with the expert's selection preserved so they can retry.
 */
export function trackCitationRequestApproveFailed(
  track: typeof segmentClient.track,
  data: {
    citationRequestId: string;
    conversationId: string;
    messageSid: string;
    errorKind: CitationRequestApproveErrorKind;
  }
) {
  track(ANALYTICS_EVENTS.CITATION_REQUEST_APPROVE_FAILED, {
    citation_request_id: data.citationRequestId,
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    error_kind: data.errorKind,
  });
}

/**
 * Fires when an in-flight citation request is explicitly cancelled by
 * the expert (e.g. via the triage sheet's "Cancel current request" CTA
 * when the existing request is no longer wanted).
 */
export function trackCitationRequestCancelled(
  track: typeof segmentClient.track,
  data: {
    citationRequestId: string;
    conversationId: string;
    messageSid: string;
    stage: CitationRequestStatus;
  }
) {
  track(ANALYTICS_EVENTS.CITATION_REQUEST_CANCELLED, {
    citation_request_id: data.citationRequestId,
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    stage: data.stage,
  });
}

/**
 * Fires when the expert submits a free-text help request for a
 * statement whose backend-suggested candidates were either absent or
 * insufficient. `had_candidates` + `candidate_count` separate the
 * "search returned nothing" case from the "search returned the wrong
 * stuff" case — both are signal-quality patterns to surface separately
 * in staff dashboards. `description` is the verbatim string the
 * author typed (post-trim) — the actual signal staff use to find the
 * article. Fires on every successful submit, including
 * replace-resubmits.
 */
export function trackStatementReferenceHelpRequested(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    statementIndex: number;
    statementType: string;
    totalStatements: number;
    description: string;
    hadCandidates: boolean;
    candidateCount: number;
  }
) {
  track(ANALYTICS_EVENTS.STATEMENT_REFERENCE_HELP_REQUESTED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    statement_index: data.statementIndex,
    statement_type: data.statementType,
    total_statements: data.totalStatements,
    description: data.description,
    had_candidates: data.hadCandidates,
    candidate_count: data.candidateCount,
  });
}

/**
 * Fires when a user opens a publication link on an expert profile.
 */
export function trackProfilePublicationOpened(data: {
  expertId: number;
  publicationId: number;
  publicationTitle: string | null;
  publicationYear: number | null;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.PROFILE_PUBLICATION_OPENED, {
    expert_id: data.expertId,
    publication_id: data.publicationId,
    publication_title: data.publicationTitle,
    publication_year: data.publicationYear,
  });
}

/**
 * Fires when a user opens a clinical trial link on an expert profile.
 */
export function trackProfileClinicalTrialOpened(data: {
  expertId: number;
  trialId: number;
  nctId: string | null;
  trialTitle: string | null;
  trialYear: number | null;
  isInstitutional: boolean;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.PROFILE_CLINICAL_TRIAL_OPENED, {
    expert_id: data.expertId,
    trial_id: data.trialId,
    nct_id: data.nctId,
    trial_title: data.trialTitle,
    trial_year: data.trialYear,
    is_institutional: data.isInstitutional,
  });
}

/**
 * Fires when a user selects the publications chip in the profile header.
 */
export function trackProfilePublicationsSelected(data: { expertId: number }) {
  return segmentClient.track(ANALYTICS_EVENTS.PROFILE_PUBLICATIONS_SELECTED, {
    expert_id: data.expertId,
  });
}

/**
 * Fires when a user selects the clinical trials chip in the profile header.
 */
export function trackProfileClinicalTrialsSelected(data: { expertId: number }) {
  return segmentClient.track(ANALYTICS_EVENTS.PROFILE_CLINICAL_TRIALS_SELECTED, {
    expert_id: data.expertId,
  });
}

/**
 * Fires when a user expands the publications section. Tracks whether this is
 * the initial expand or a subsequent page load.
 */
export function trackProfilePublicationsShowMore(data: {
  expertId: number;
  isPagination: boolean;
  totalCount: number;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.PROFILE_PUBLICATIONS_SHOW_MORE, {
    expert_id: data.expertId,
    is_pagination: data.isPagination,
    total_count: data.totalCount,
  });
}

/**
 * Fires when a user expands the clinical trials section. Tracks whether this is
 * the initial expand or a subsequent page load.
 */
export function trackProfileClinicalTrialsShowMore(data: {
  expertId: number;
  isPagination: boolean;
  totalCount: number;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.PROFILE_CLINICAL_TRIALS_SHOW_MORE, {
    expert_id: data.expertId,
    is_pagination: data.isPagination,
    total_count: data.totalCount,
  });
}

/**
 * Fires when a user presses "Show less" to collapse the publications section.
 */
export function trackProfilePublicationsShowLess(data: { expertId: number }) {
  return segmentClient.track(ANALYTICS_EVENTS.PROFILE_PUBLICATIONS_SHOW_LESS, {
    expert_id: data.expertId,
  });
}

/**
 * Fires when a user presses "Show less" to collapse the clinical trials section.
 */
export function trackProfileClinicalTrialsShowLess(data: { expertId: number }) {
  return segmentClient.track(ANALYTICS_EVENTS.PROFILE_CLINICAL_TRIALS_SHOW_LESS, {
    expert_id: data.expertId,
  });
}

/**
 * Fires when the expert clears their own pending help request via the
 * pending-chip's Cancel control. Does NOT fire when staff resolve a
 * help request by attaching a real reference (the help-request entry
 * stays as audit until the expert cancels it).
 */
export function trackStatementReferenceHelpCanceled(
  track: typeof segmentClient.track,
  data: {
    conversationId: string;
    messageSid: string;
    statementIndex: number;
    statementType: string;
    totalStatements: number;
  }
) {
  track(ANALYTICS_EVENTS.STATEMENT_REFERENCE_HELP_CANCELED, {
    conversation_id: data.conversationId,
    message_sid: data.messageSid,
    statement_index: data.statementIndex,
    statement_type: data.statementType,
    total_statements: data.totalStatements,
  });
}

/**
 * Fires when the de-escalated expert SMS banner is shown above the composer —
 * i.e. an expert who already dismissed the full setup modal lands in another
 * inquiry without SMS reaching them. Distinct from `Mobile number setup viewed`
 * (the modal/form) so the gentler nudge's reach and conversion can be measured
 * separately.
 */
export function trackExpertSmsBannerViewed() {
  return segmentClient.track(ANALYTICS_EVENTS.SMS_PROMPT_BANNER_VIEWED, {
    source: 'first_message',
  });
}

/**
 * Fires when the expert dismisses the composer SMS banner via its close
 * control. This is terminal and per-account: it silences the nudge across all
 * the expert's inquiries, so it should fire at most once per expert — the
 * signal that they've explicitly opted out of the gentle reminder.
 */
export function trackExpertSmsBannerDismissed() {
  return segmentClient.track(ANALYTICS_EVENTS.SMS_PROMPT_BANNER_DISMISSED, {
    source: 'first_message',
  });
}

/** The notification events a mobile-number opt-in can turn on. Mirror the
 * `event` values passed to `setNotificationPreferenceV2`. */
export type SmsNotificationEvent = 'newInquiry' | 'newMessage' | 'referencesReady';

/**
 * Fires when the shared mobile-number setup form mounts (the SMS opt-in is
 * shown). `source` identifies the surface (reference wizard, first message,
 * notification settings, post-inquiry).
 */
export function trackMobileNumberSetupViewed(source: MobileNumberSetupSource) {
  return segmentClient.track(ANALYTICS_EVENTS.MOBILE_NUMBER_SETUP_VIEWED, { source });
}

/**
 * Fires when the mobile-number setup is dismissed without saving — either an
 * explicit "Not now" (`skip`) or any other teardown such as the X, backdrop,
 * navigation, or the host unmounting the form (`implicit`).
 */
export function trackMobileNumberSetupDismissed(properties: {
  source: MobileNumberSetupSource;
  dismissMethod: 'skip' | 'implicit';
  isDirty?: boolean;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.MOBILE_NUMBER_SETUP_DISMISSED, {
    source: properties.source,
    dismiss_method: properties.dismissMethod,
    is_dirty: properties.isDirty,
  });
}

/** Fires when saving the mobile number fails. */
export function trackMobileNumberSetupFailed(properties: {
  source: MobileNumberSetupSource;
  error: string;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.MOBILE_NUMBER_SETUP_FAILED, {
    source: properties.source,
    error_message: properties.error,
  });
}

/**
 * Fires when an SMS opt-in is turned on via the setup form (number saved with
 * SMS enabled). The canonical per-surface opt-in conversion signal; `source`
 * distinguishes the surface and `sms_events` lists the notifications it turned
 * on. `had_phone_number` is the pre-save state, so opt-ins can be split into
 * new-number captures vs users who already had a number and only flipped the
 * SMS preference on — across every surface, including the submitter prompt.
 */
export function trackSmsNotificationsEnabled(properties: {
  source: MobileNumberSetupSource;
  smsEvents: SmsNotificationEvent[];
  hadPhoneNumber: boolean;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.SMS_NOTIFICATIONS_ENABLED, {
    source: properties.source,
    sms_events: properties.smsEvents,
    had_phone_number: properties.hadPhoneNumber,
  });
}

/**
 * Fires when the expert taps "Notify me when ready" in the reference wizard.
 * `conversation_id` / `message_sid` attribute the tap to its thread and expert
 * message, so it sizes against the search funnel it sits in.
 * `references_ready_sms_enabled` / `has_phone_number` capture whether SMS could
 * already reach them (and thus whether the phone-setup prompt was shown).
 */
export function trackReferenceWizardNotifyRequested(properties: {
  conversationId: string;
  messageSid: string;
  hasPhoneNumber: boolean;
  referencesReadySmsEnabled: boolean;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.REFERENCE_WIZARD_NOTIFY_REQUESTED, {
    conversation_id: properties.conversationId,
    message_sid: properties.messageSid,
    has_phone_number: properties.hasPhoneNumber,
    references_ready_sms_enabled: properties.referencesReadySmsEnabled,
  });
}

/**
 * Fires when the app is entered by following a link we sent — an email link, an SMS
 * link, a notification's launch URL, a referral link, or a campaign redirect.
 *
 * Reported at the moment of entry rather than on arrival at a destination, which is what
 * separates it from `References ready deep link opened`: that one fires only once the
 * review sheet is actually on screen, so the two together say whether a link that was
 * followed also delivered the person somewhere. A link that is opened and then lands
 * nowhere is the failure worth seeing, and an entry-time event is the only place it is
 * visible.
 *
 * `source` is the channel that sent the link, taken from the parameter the backend
 * stamps, and `push` is filled in from a just-recorded notification tap when the link
 * carries no parameter of its own. It stays alongside `Notification opened` rather than
 * replacing it — that event measures the send, this one measures the entry.
 *
 * `communication_id` and `communication_channel` are what join the two. They name the
 * same columns the notification open reports, deliberately, because a path cannot
 * identify an attempt: every notification of a kind lands on the same route, so joining
 * on path attributes an entry to a destination rather than to the send that caused it.
 * Carrying the attempt id on both events is what makes "was this send followed, and did
 * following it arrive anywhere" one question instead of two.
 *
 * Emitted without waiting for the session to identify, for the same reason the
 * notification open is: an entry attributed to an anonymous id can be stitched back to a
 * person when the identity resolves, and a dropped one cannot.
 *
 * No URL is reported, only a path with credential segments masked. See
 * `util/deepLinkAnalytics.ts` for why.
 */
export function trackDeepLinkOpened(properties: {
  path: string;
  linkType: DeepLinkType;
  launchType: DeepLinkLaunchType;
  params: DeepLinkParams;
  communicationId?: string;
  communicationChannel?: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.DEEP_LINK_OPENED, {
    path: properties.path,
    link_type: properties.linkType,
    launch_type: properties.launchType,
    ...properties.params,
    ...communicationAttributionProperties(properties),
  });
}

/**
 * Fires when a push notification is tapped, from the provider's own click
 * listener rather than from any screen.
 *
 * `notification_id` is the provider's id for the send, which is also what the
 * admin send path returns to whoever dispatched it. That makes it the join key
 * between a campaign and its opens without needing a campaign field in the
 * payload, which the notification API does not carry today.
 *
 * `recipient_id` carries the addressee explicitly because a tap that launches a
 * killed app fires before the session identifies, so the analytics client's own
 * user id is null exactly when the open matters most. Observed in production, not
 * hypothetical.
 *
 * Deliberately not called `user_id`. The analytics client already sets that field
 * from the identified session, and reusing the name puts two writers on one
 * property, where the null from an unidentified session can win.
 *
 * Deliberately not named for the gesture. A tap on a phone and a click on the
 * web are the same event here, and the provider reports both through one
 * listener.
 *
 * `communication_id` and `communication_channel` are the backend's attempt id and
 * channel, read off the launch URL. They are passed in rather than parsed here
 * because only native needs them: a web click travels through the browser, where
 * the warehouse reads the same two values off the page URL, and sending them from
 * both places would have two writers on one column. The names are what the
 * warehouse reads columns by, so they are not ours to tidy.
 *
 * Both are omitted when absent rather than sent empty. A notification predating
 * the tag carries neither, and an empty channel is read downstream as an arrival
 * that genuinely carried none — a claim we are not in a position to make.
 */
export function trackNotificationOpened(properties: {
  notificationId?: string;
  launchUrl?: string;
  recipientId?: string;
  communicationId?: string;
  communicationChannel?: string;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.NOTIFICATION_OPENED, {
    notification_id: properties.notificationId,
    launch_url: properties.launchUrl,
    recipient_id: properties.recipientId,
    ...communicationAttributionProperties(properties),
  });
}

/**
 * Fires when someone opens the reference badge to see what earns it.
 *
 * Named for opening rather than viewing because the badge is on screen constantly and only
 * sometimes pressed. It shows no per-expert figure by design, so a press is the only signal
 * that anyone wanted the criteria behind it.
 *
 * `expert_id` is whose badge was opened, not who opened it. The two differ on every surface
 * except an expert's own profile, and the interesting case is a submitter checking an expert
 * they are about to send a case to.
 */
export function trackReferenceBadgeOpened(properties: {
  expertId: string | number;
  source: 'profile' | 'directory' | 'inquiry_sidebar' | 'expert_picker';
}) {
  return segmentClient.track(ANALYTICS_EVENTS.REFERENCE_BADGE_OPENED, {
    expert_id: String(properties.expertId),
    source: properties.source,
  });
}

/**
 * Where a push opt-in was raised from. Push is the one channel nobody but the
 * user can enable, and a browser decline is close to permanent, so which
 * surface did the asking is the property every comparison between surfaces
 * turns on. Mirrors `MobileNumberSetupSource` for the SMS channel.
 *
 * The Q4 in-context prompts add their own values here.
 */
export type PushOptInSource = 'notification_settings' | 'inquiry_success';

/**
 * What an attempt to turn push on ended in.
 *
 * `declined` covers a real refusal and a dialog the browser suppressed without
 * showing, because the page cannot tell those apart and both leave the user
 * unreachable. `unavailable` is not a refusal at all: the provider's SDK never
 * came up, so nobody was asked and no permission was spent.
 */
export type PushOptInResponse = 'allowed' | 'declined' | 'unavailable';

/**
 * Fires once per attempt to turn push on, whichever way it ended.
 *
 * Tracked at the shared opt-in rather than per surface, so a new push prompt
 * is measured by existing rather than by remembering to instrument itself.
 *
 * Acceptance is `allowed` over `allowed` plus `declined`. Leaving
 * `unavailable` in the denominator would count people who were never asked as
 * people who said no, understating the rate by however often the SDK is
 * blocked — which is a property of extensions and networks rather than of the
 * ask, and moves independently of it.
 *
 * An unresolved user query sends `unknown` rather than nothing. Segment drops
 * undefined values, which would leave those events absent from a user-type
 * breakdown instead of visible in it, hiding the race rather than sizing it.
 */
export function trackOsNotificationPermissionSet(properties: {
  response: PushOptInResponse;
  source: PushOptInSource;
  userType?: string;
}) {
  return segmentClient.track(ANALYTICS_EVENTS.OS_NOTIFICATION_PERMISSION_SET, {
    response: properties.response,
    source: properties.source,
    user_type: properties.userType ?? 'unknown',
  });
}

export type ExpertLinkShareChannel = 'native_share' | 'web_share' | 'qr_image';

/**
 * Which of an expert's contact details left the screen, as opposed to
 * `ExpertLinkShareChannel`, which is the transport that carried it. The two are
 * separate axes: the same address can go out through a native sheet or a copy,
 * and the same sheet can carry a code, a link or an address.
 *
 * Never carries the value itself — a target name is enough to tell which detail
 * experts actually hand out, and the address is a real deliverable inbox.
 */
export type ExpertShareTarget = 'qr_image' | 'link' | 'intake_email' | 'sms_number';

/**
 * An expert shared one of their own contact details through a share sheet.
 * `expert_id` is the sharer, since the detail is theirs. Rows written before
 * the intake address reached this screen carry no `share_target`.
 *
 * **The targets do not count the same thing, so do not compare them.** A link
 * or an address goes out through the system share API, which reports whether
 * the person went through with it, and is counted only when they did. The QR
 * code goes out as a file, and that API returns nothing at all, so opening the
 * sheet and abandoning it is indistinguishable from sending — the image is
 * therefore counted on every opening and overstates against the other two.
 */
export function trackExpertLinkShared(properties: {
  expertId: number;
  channel: ExpertLinkShareChannel;
  target: ExpertShareTarget;
}) {
  segmentClient.track(ANALYTICS_EVENTS.EXPERT_LINK_SHARED, {
    expert_id: properties.expertId,
    share_channel: properties.channel,
    share_target: properties.target,
  });
}

/**
 * An expert tapped the app-icon quick action. It fires before the link is
 * fetched and shared, so it counts taps, not completed shares; pair it with
 * `Expert link shared`. `source` names the entry point so Siri and Shortcuts
 * runs can join it later; those run outside the JS runtime and are not tracked
 * yet.
 */
export function trackExpertLinkShortcutUsed(properties: { source: 'quick_action' }) {
  segmentClient.track(ANALYTICS_EVENTS.EXPERT_LINK_SHORTCUT_USED, {
    source: properties.source,
  });
}

/**
 * A referrer's share sheet for their referral link resolved without error. Same event and
 * properties the invites screen sends, so every surface lands in one invite funnel; like it,
 * this fires once the sheet closes, not only on a completed share.
 */
export function trackInviteNativeShared(properties: {
  location: ReferralLocation;
  userName: string | null;
}) {
  segmentClient.track(ANALYTICS_EVENTS.INVITE_NATIVE_SHARED, {
    user_name: properties.userName,
    invite_copy_version: INVITE_COPY_VERSION,
    referral_location: properties.location,
    referral_role: 'referrer',
    invite_channel: 'share_link',
  });
}

/**
 * An expert copied one of their own contact details. Rows written before the
 * intake address reached this screen carry no `copy_target`, so read a missing
 * one as the link.
 */
export function trackExpertLinkCopied(properties: { expertId: number; target: ExpertShareTarget }) {
  segmentClient.track(ANALYTICS_EVENTS.EXPERT_LINK_COPIED, {
    expert_id: properties.expertId,
    copy_target: properties.target,
  });
}

/**
 * Someone opened an expert's personal link in the app. Reports the expert, the slug the
 * link resolved to, whether it was the current one, and the platform the link was
 * followed from when its `via` tag named one, never the link itself.
 *
 * `platform` is what lets the backend tell a native open apart from a web one. A tap
 * with the app installed opens it through the universal link without requesting the
 * page, so on iOS and Android this event is the only record the click happened; on web
 * the API has already recorded the page request, and segment-ingress drops this copy.
 */
export function trackExpertLinkOpened(properties: {
  expertId: number;
  slug: string;
  isAuthenticated: boolean;
  slugWasCurrent: boolean;
  via?: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.EXPERT_LINK_OPENED, {
    expert_id: properties.expertId,
    slug: properties.slug,
    is_authenticated: properties.isAuthenticated,
    slug_was_current: properties.slugWasCurrent,
    platform: Platform.OS,
    ...(properties.via ? { via: properties.via } : {}),
  });
}

/**
 * Context shared by every event the global announcement banner fires, so a
 * click and the navigation it caused describe the same announcement.
 *
 * Known divergence from `analytics.md`: these properties ship camelCase rather
 * than snake_case, and `ANNOUNCEMENT_CLICKED` is named for a UI verb the rule
 * discourages. Both are live warehouse contracts — renaming them breaks saved
 * reports, so they are preserved deliberately rather than by oversight. Fix
 * them only alongside the warehouse.
 */
export type AnnouncementAnalyticsContext = {
  username?: string;
  userId?: string;
  ctaUrl?: string;
  announcementType?: string;
  announcementTitle?: string;
  ctaAction?: string;
};

/** Where a CTA press ended up, reported by whichever branch handled it. */
export type AnnouncementNavigationType = 'deep-link' | 'relative-path' | 'external';

function normalizeAnnouncementContext(context: AnnouncementAnalyticsContext) {
  return {
    username: context.username,
    userId: context.userId,
    ctaUrl: context.ctaUrl,
    announcementType: context.announcementType,
    announcementTitle: context.announcementTitle,
    ctaAction: context.ctaAction,
  };
}

/**
 * The announcement banner's intent event: exactly one per CTA press, whatever
 * the press turns out to do.
 *
 * It must stay one-per-press. A navigation branch reports where it went through
 * `trackAnnouncementLinkNavigated`; firing this again from a branch is what made
 * external CTAs count two clicks for as long as they existed.
 */
export function trackAnnouncementClicked(context: AnnouncementAnalyticsContext) {
  return segmentClient.track(
    ANALYTICS_EVENTS.ANNOUNCEMENT_CLICKED,
    normalizeAnnouncementContext(context)
  );
}

/** Tracks where an announcement CTA navigated, once the branch is known. */
export function trackAnnouncementLinkNavigated(
  context: AnnouncementAnalyticsContext & { navigationType: AnnouncementNavigationType }
) {
  const { navigationType, ...announcement } = context;

  return segmentClient.track(ANALYTICS_EVENTS.ANNOUNCEMENT_LINK_NAVIGATED, {
    ...normalizeAnnouncementContext(announcement),
    navigationType,
  });
}

/**
 * Tracks the referral link copied from an announcement CTA.
 *
 * `hasReferralCode` is false when the expert had no code to copy, which is a
 * failure the banner still reports so the gap between presses and copies is
 * visible rather than silent.
 */
export function trackAnnouncementInviteLinkCopied(
  context: AnnouncementAnalyticsContext & { hasReferralCode: boolean }
) {
  const { hasReferralCode, ...announcement } = context;

  return segmentClient.track(ANALYTICS_EVENTS.INVITE_LINK_COPIED, {
    ...normalizeAnnouncementContext(announcement),
    hasReferralCode,
    referral_role: 'referrer',
    invite_channel: 'copy_link',
  });
}

/** Tracks the reader opening Account from the avatar in a tab header. */
export function trackAccountHeaderAvatarClicked() {
  return segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_MENU_CLICKED, { source: 'header_avatar' });
}

/** Tracks the reader opening the invite screen from the icon in a tab header. */
export function trackInviteHeaderIconClicked() {
  return segmentClient.track(ANALYTICS_EVENTS.INVITE_A_PEER_CLICKED, { source: 'header' });
}

/** Tracks the Fellows Corner promo banner's call to action being taken. */
export function trackFellowsBannerClicked() {
  return segmentClient.track(ANALYTICS_EVENTS.FELLOWS_BANNER_CLICKED);
}

/** Tracks the reader dismissing the Fellows Corner promo banner. */
export function trackFellowsBannerDismissed() {
  return segmentClient.track(ANALYTICS_EVENTS.FELLOWS_BANNER_DISMISSED);
}

/** Tracks the inbox announcement banner becoming visible, once per announcement. */
export function trackInboxAnnouncementBannerViewed(properties: { announcementId: string }) {
  return segmentClient.track(ANALYTICS_EVENTS.INBOX_ANNOUNCEMENT_BANNER_VIEWED, {
    announcementId: properties.announcementId,
  });
}

/** Tracks the reader dismissing the inbox announcement banner. */
export function trackInboxAnnouncementBannerDismissed(properties: { announcementId: string }) {
  return segmentClient.track(ANALYTICS_EVENTS.INBOX_ANNOUNCEMENT_BANNER_DISMISSED, {
    announcementId: properties.announcementId,
  });
}

/**
 * Reply draft events the app alone sees. The server mirrors every draft action
 * (selection, strikes, hand-off, send) to Segment itself, so nothing here
 * repeats one, and no payload carries draft text: it is clinical content.
 */
type ReplyDraftEventProperties = {
  replyDraftId: string;
  conversationId: string;
};

/** Tracks a reply draft opening from the `.draft` shortcut, with the step it opened on. */
export function trackReplyDraftOpened(
  properties: ReplyDraftEventProperties & { status: string; step: 'card' | 'review' }
) {
  return segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_OPENED, {
    reply_draft_id: properties.replyDraftId,
    conversation_id: properties.conversationId,
    status: properties.status,
    step: properties.step,
    entry: 'dot_phrase',
  });
}

/** Tracks a reply draft closing without a hand-off, with the step it was left on. */
export function trackReplyDraftClosed(
  properties: ReplyDraftEventProperties & { step: 'loading' | 'card' | 'review' }
) {
  return segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_CLOSED, {
    reply_draft_id: properties.replyDraftId,
    conversation_id: properties.conversationId,
    step: properties.step,
  });
}

/**
 * Tracks an expert reaching a step of the reply draft wizard: choosing a
 * position (`card`) or adding and removing the reply's points (`review`).
 * Where experts stop between steps is what this answers.
 */
export function trackReplyDraftStepViewed(
  properties: ReplyDraftEventProperties & { step: 'card' | 'review' }
) {
  return segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_STEP_VIEWED, {
    reply_draft_id: properties.replyDraftId,
    conversation_id: properties.conversationId,
    step: properties.step,
  });
}

/**
 * Tracks the reply draft tour reaching the screen. `trigger` separates an
 * expert's first draft from a replay they asked for, and `segment` names the
 * wizard step it opened on.
 */
export function trackReplyDraftTourStarted(
  properties: ReplyDraftEventProperties & { trigger: 'auto' | 'replay'; segment: string }
) {
  segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_TOUR_STARTED, {
    reply_draft_id: properties.replyDraftId,
    conversation_id: properties.conversationId,
    trigger: properties.trigger,
    segment: properties.segment,
  });
}

/**
 * Tracks an expert leaving the reply draft tour early, with the segment and
 * tour step they left on. `tour_step` is the tooltip's place in its segment,
 * named apart from `step`, which every other reply draft event uses for the
 * wizard step.
 */
export function trackReplyDraftTourSkipped(
  properties: ReplyDraftEventProperties & {
    trigger: 'auto' | 'replay';
    segment: string;
    tourStep: number;
  }
) {
  segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_TOUR_SKIPPED, {
    reply_draft_id: properties.replyDraftId,
    conversation_id: properties.conversationId,
    trigger: properties.trigger,
    segment: properties.segment,
    tour_step: properties.tourStep,
  });
}

/** Tracks an expert finishing the reply draft tour's last step. */
export function trackReplyDraftTourCompleted(
  properties: ReplyDraftEventProperties & { trigger: 'auto' | 'replay' }
) {
  segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_TOUR_COMPLETED, {
    reply_draft_id: properties.replyDraftId,
    conversation_id: properties.conversationId,
    trigger: properties.trigger,
  });
}

/** Tracks a reply draft re-rendering from the server because it changed on another device. */
export function trackReplyDraftConflicted(properties: ReplyDraftEventProperties) {
  return segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_CONFLICTED, {
    reply_draft_id: properties.replyDraftId,
    conversation_id: properties.conversationId,
  });
}

/**
 * Tracks an expert opening a source's details from a reply draft: a sentence's
 * citation or a "changed recently" update. How often experts check the
 * evidence before sending is what this answers.
 */
export function trackReplyDraftSourceViewed(
  properties: ReplyDraftEventProperties & {
    publicationId: number;
    from: 'sentence' | 'changed_recently';
  }
) {
  return segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_SOURCE_VIEWED, {
    reply_draft_id: properties.replyDraftId,
    conversation_id: properties.conversationId,
    publication_id: properties.publicationId,
    source: properties.from,
  });
}

/** Tracks "use draft" failing, by kind, so the pilot can see how often the check gives up. */
export function trackReplyDraftUseFailed(properties: { replyDraftId: string; kind: string }) {
  return segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_USE_FAILED, {
    reply_draft_id: properties.replyDraftId,
    kind: properties.kind,
  });
}

/** Tracks an expert keeping their edited reply rather than replacing it with the updated draft. */
export function trackReplyDraftReplaceDeclined(properties: ReplyDraftEventProperties) {
  return segmentClient.track(ANALYTICS_EVENTS.REPLY_DRAFT_REPLACE_DECLINED, {
    reply_draft_id: properties.replyDraftId,
    conversation_id: properties.conversationId,
  });
}

/**
 * Tracks an expert opening the time away sheet. No dates are reported, only
 * whether a period already exists and whether it covers today.
 */
export function trackAwaySheetViewed(properties: {
  hasExistingPeriod: boolean;
  isCurrent: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_AWAY_SHEET_VIEWED, {
    has_existing_period: properties.hasExistingPeriod,
    is_current: properties.isCurrent,
  });
}

/**
 * Tracks a time away period being saved. Reports lengths in days rather than
 * dates: `starts_in_days` is 0 for a period that starts today or has already
 * started.
 */
export function trackAwayPeriodSaved(properties: {
  mode: 'new' | 'edited';
  days: number;
  startsInDays: number;
  isCurrent: boolean;
}) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_AWAY_PERIOD_SAVED, {
    mode: properties.mode,
    days: properties.days,
    starts_in_days: Math.max(0, properties.startsInDays),
    is_current: properties.isCurrent,
  });
}

/**
 * Tracks a time away period being removed: `ended_early` for one already under
 * way (End now), `cancelled_upcoming` for one that had not started.
 */
export function trackAwayPeriodRemoved(properties: { kind: 'ended_early' | 'cancelled_upcoming' }) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_AWAY_PERIOD_REMOVED, {
    kind: properties.kind,
  });
}

/** Tracks a time away save that failed, with the server's error code when it sent one. */
export function trackAwayPeriodSaveFailed(properties: {
  mode: 'new' | 'edited';
  errorCode?: string;
}) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_AWAY_PERIOD_SAVE_FAILED, {
    mode: properties.mode,
    error_code: properties.errorCode ?? 'unknown',
  });
}

export type AwayCalendarTutorialStatus = 'shown' | 'completed' | 'dismissed';

/**
 * Tracks the away calendar tutorial on the Account screen: `shown` when it
 * reaches the screen, then `completed` or `dismissed` when the expert closes it.
 */
export function trackAwayCalendarTutorial(properties: { status: AwayCalendarTutorialStatus }) {
  segmentClient.track(ANALYTICS_EVENTS.ACCOUNT_AWAY_TUTORIAL_UPDATED, {
    status: properties.status,
  });
}

export function trackMicroSurveyDisplayed(surveyType: string) {
  segmentClient.track(ANALYTICS_EVENTS.MICRO_SURVEY_DISPLAYED, { surveyType });
}

export function trackMicroSurveyResponded(properties: {
  surveyType: string;
  response: string;
  additionalInfo?: string | null;
}) {
  const { surveyType, response, additionalInfo } = properties;

  segmentClient.track(ANALYTICS_EVENTS.MICRO_SURVEY_RESPONDED, {
    surveyType,
    response,
    ...(additionalInfo ? { additionalInfo } : {}),
  });
}

export function trackMicroSurveyClosed(surveyType: string) {
  segmentClient.track(ANALYTICS_EVENTS.MICRO_SURVEY_CLOSED, { surveyType });
}
